(function () {
  const PLAN_ORDER = { basic: 0, pro: 1, prime: 2 };
  const FEATURE_DIVIDER = '__divider__';
  const CHECKOUT_ID_STORAGE_KEY = 'jjb_membership_checkout_id';
  const CHECKOUT_SOURCE_STORAGE_KEY_PREFIX = 'jjb_membership_checkout_source_';
  const PLANS = [
    {
      plan_type: 'basic',
      monthly_price: 0,
      yearly_original_price: 0,
      yearly_first_year_price: 0,
    },
    {
      plan_type: 'pro',
      monthly_price: 19.9,
      yearly_original_price: 238.8,
      yearly_first_year_price: 191.04,
    },
    {
      plan_type: 'prime',
      monthly_price: 99.9,
      yearly_original_price: 1198.8,
      yearly_first_year_price: 959.04,
    },
  ];
  const BASIC_DAILY_LIMIT_EXPERIMENT = 'independent_site_free_generation_limit';
  const BASIC_DAILY_LIMIT_GROUP_IDS = { 40: 99, 25: 100 };
  let basicDailyLimit = null;
  let basicDailyLimitRequest;
  const paywallStates = new WeakMap();
  const membershipRefreshes = new WeakMap();
  const membershipDefaultNavigation = new WeakMap();
  const pageScrollLockOwners = new Set();
  let autoCheckoutHandled = false;
  let pageScrollLockSnapshot = null;

  function registerMembershipBebasText() {
    if (!window.customElements || typeof window.HTMLElement !== 'function') {
      return;
    }
    if (window.customElements.get('jjb-membership-bebas-text')) return;

    class JjbMembershipBebasText extends window.HTMLElement {}

    window.customElements.define(
      'jjb-membership-bebas-text',
      JjbMembershipBebasText,
    );
  }

  function renderMembershipBebasText(content) {
    return (
      '<jjb-membership-bebas-text>' + content + '</jjb-membership-bebas-text>'
    );
  }

  registerMembershipBebasText();

  function isTestMode() {
    return window.__RELEASE__.isTest;
  }

  async function getMembershipExperiment(experimentName) {
    let timer;
    try {
      const request =
        isTestMode() && window.jjbUtils?.overrideExperiment
          ? window.jjbUtils.overrideExperiment(experimentName)
          : Promise.resolve(window.statsigReady).then(() =>
              window.statsigClient?.getExperiment(experimentName),
            );
      return await Promise.race([
        request,
        new Promise((resolve) => {
          timer = window.setTimeout(() => resolve(undefined), 2000);
        }),
      ]);
    } finally {
      window.clearTimeout(timer);
    }
  }

  function getMembershipApiPrefix() {
    const baseUrl = isTestMode() ? '/apps/ai-test' : '/apps/ai';
    return baseUrl + '/membership';
  }

  function loadBasicDailyLimit() {
    if (!basicDailyLimitRequest) {
      basicDailyLimitRequest = (async () => {
        try {
          const experiment = await getMembershipExperiment(
            BASIC_DAILY_LIMIT_EXPERIMENT,
          );
          const value = experiment?.get('basicDailyLimit', undefined);
          if (value === 25 || value === 40) {
            basicDailyLimit = value;
            window.zlog?.recordExperimentGroup?.(
              `${BASIC_DAILY_LIMIT_EXPERIMENT}:basicDailyLimit`,
              BASIC_DAILY_LIMIT_GROUP_IDS[value],
            );
            return;
          }
        } catch (_) {}
        basicDailyLimit = 40;
      })();
    }
    return basicDailyLimitRequest;
  }

  function withTestBypass(path) {
    if (!isTestMode()) return path;
    const parts = String(path || '').split('#');
    const pathname = parts[0];
    const hash = parts.length > 1 ? parts.slice(1).join('#') : '';
    const queryIndex = pathname.indexOf('?');
    const basePath =
      queryIndex === -1 ? pathname : pathname.slice(0, queryIndex);
    const queryString = queryIndex === -1 ? '' : pathname.slice(queryIndex + 1);
    const params = new URLSearchParams(queryString);
    const query = params.toString();
    const nextPath = query ? basePath + '?' + query : basePath;
    return hash ? nextPath + '#' + hash : nextPath;
  }

  function getIdempotencyKey() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return (
      'membership-' +
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2)
    );
  }

  function getErrorMessage(error) {
    if (error && error.message) return error.message;
    return 'Something went wrong. Please try again.';
  }

  function showMembershipRequestError(error) {
    if (typeof window.toast !== 'function') return;
    if (error?.status === 400 && String(error.code) === '1027') {
      window.toast(
        'Your membership information is syncing. Please refresh the page.',
        10000,
      );
      return;
    }
    if (error?.status === 429) {
      window.toast(
        'Please refresh the page and complete the human verification process.',
        10000,
      );
      return;
    }
    if (error?.status >= 500 && error.status <= 599) {
      window.toast(
        `${getErrorMessage(error)} Please take a screenshot and contact customer support.`,
        10000,
      );
      return;
    }
    window.toast(getErrorMessage(error));
  }

  // 跨平台受限操作固定文案（技术方案 3.2；后端 /me 的 interop.restrictedReason 携带）
  var CROSS_PLATFORM_RESTRICTED_MESSAGE =
    'Plan changes are only available on the original purchase platform.';

  function membershipInteropRestricted(membership) {
    var interop = membership && membership.interop;
    if (!interop || typeof interop !== 'object') return null;
    return typeof interop.restrictedReason === 'string' &&
      interop.restrictedReason
      ? interop.restrictedReason
      : null;
  }

  function getMembershipInteropSourceLabel(membership) {
    const state = normalizeMembershipState(membership);
    if (
      state.interop?.sourceSystem !== 'APP' ||
      mapMembershipState(state).plan_type === 'basic'
    ) {
      return '';
    }
    return 'Purchased in App';
  }

  // 可关闭、可键盘操作的原地提示弹窗：只提示，不发起任何受限请求（MS-084/093）
  function showCrossPlatformRestrictedDialog(root, message) {
    var existing = document.getElementById('jjb-cross-platform-dialog');
    if (existing) existing.remove();
    var dialog = document.createElement('div');
    dialog.id = 'jjb-cross-platform-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', 'Plan change restricted');
    dialog.style.cssText =
      'position:fixed;inset:0;z-index:2147483000;display:flex;align-items:center;justify-content:center;background:rgba(15,15,20,.55);';
    var panel = document.createElement('div');
    panel.style.cssText =
      'max-width:360px;margin:16px;padding:24px;background:#fff;border-radius:12px;box-shadow:0 12px 40px rgba(0,0,0,.25);font-family:inherit;text-align:center;';
    var text = document.createElement('p');
    text.textContent = message || CROSS_PLATFORM_RESTRICTED_MESSAGE;
    text.style.cssText =
      'margin:0 0 16px;color:#1f1f27;font-size:15px;line-height:1.5;';
    var button = document.createElement('button');
    button.type = 'button';
    button.textContent = 'OK';
    button.style.cssText =
      'min-width:96px;padding:10px 18px;border:0;border-radius:999px;background:#111;color:#fff;font-size:14px;cursor:pointer;';
    panel.appendChild(text);
    panel.appendChild(button);
    dialog.appendChild(panel);
    function close() {
      dialog.remove();
      document.removeEventListener('keydown', onKey);
    }
    function onKey(event) {
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        close();
      }
    }
    button.addEventListener('click', close);
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });
    document.addEventListener('keydown', onKey);
    document.body.appendChild(dialog);
    button.focus();
  }

  async function requestMembershipApi(path, options) {
    const requestOptions = options || {};
    const isFormData =
      typeof FormData !== 'undefined' &&
      requestOptions.body instanceof FormData;
    const headers = Object.assign(
      { Accept: 'application/json' },
      requestOptions.body && !isFormData
        ? { 'Content-Type': 'application/json' }
        : {},
      requestOptions.headers || {},
    );
    if (path === '/quota') {
      try {
        await window.statsigReady;
        const stableID = window.statsigClient?.getContext().stableID;
        if (stableID) headers['X-Statsig-Stable-Id'] = stableID;
      } catch (_) {}
    }
    const response = await fetch(
      getMembershipApiPrefix() + withTestBypass(path),
      {
        method: requestOptions.method || 'GET',
        headers,
        body:
          requestOptions.body &&
          !isFormData &&
          typeof requestOptions.body !== 'string'
            ? JSON.stringify(requestOptions.body)
            : requestOptions.body,
        credentials: 'same-origin',
      },
    );
    let payload = null;
    try {
      payload = await response.json();
    } catch (_) {}
    if (!response.ok || !payload || payload.success === false) {
      const error = new Error(
        (payload && (payload.message || payload.errorMessage)) ||
          'Membership request failed.',
      );
      error.code = payload && payload.code;
      error.status = response.status;
      throw error;
    }
    return payload.data;
  }

  function toBillingCycle(cycle) {
    return cycle === 'yearly' ? 'YEAR' : 'MONTH';
  }

  function normalizeBillingCycle(cycle, planType) {
    if (normalizePlanType(planType) === 'basic') return 'free';
    const value = String(cycle || '').toUpperCase();
    return value === 'YEAR' || value === 'YEARLY' ? 'yearly' : 'monthly';
  }

  function toPlanType(plan) {
    const value = String(plan || '').toUpperCase();
    if (value === 'PREMIUM') return 'prime';
    if (value === 'PRO') return 'pro';
    return 'basic';
  }

  function normalizeMembershipStatus(status) {
    const value = String(status || 'BASIC').toUpperCase();
    const statuses = [
      'BASIC',
      'PROCESSING',
      'ACTIVE',
      'CANCELLING',
      'DUNNING',
      'SUSPENDED',
      'EXPIRED',
    ];
    return statuses.indexOf(value) === -1 ? 'BASIC' : value;
  }

  function isFutureTime(value) {
    if (!value) return false;
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) && timestamp > Date.now();
  }

  function getOptionalBoolean() {
    for (let index = 0; index < arguments.length; index += 1) {
      const value = arguments[index];
      const normalizedValue =
        typeof value === 'string' ? value.trim().toLowerCase() : value;
      if (
        normalizedValue === true ||
        normalizedValue === 'true' ||
        normalizedValue === 1 ||
        normalizedValue === '1'
      ) {
        return true;
      }
      if (
        normalizedValue === false ||
        normalizedValue === 'false' ||
        normalizedValue === 0 ||
        normalizedValue === '0'
      ) {
        return false;
      }
    }
    return null;
  }

  function normalizeMembershipState(state) {
    if (!state || typeof state !== 'object') {
      return {
        plan: 'BASIC',
        status: 'BASIC',
        paidThrough: '',
        billingCycle: '',
        autoRenew: false,
        autoRenewSpecified: false,
        cancelAtPeriodEnd: false,
        membershipSource: 'PAID',
        giftId: null,
        giftEndsAt: null,
        resumeVisible: false,
        renewalPlan: null,
        renewalBillingCycle: null,
      };
    }
    const nestedState =
      state.membership && typeof state.membership === 'object'
        ? state.membership
        : state.subscription && typeof state.subscription === 'object'
          ? state.subscription
          : state.membershipState && typeof state.membershipState === 'object'
            ? state.membershipState
            : state;
    const status = normalizeMembershipStatus(
      nestedState.membershipStatus ||
        nestedState.status ||
        nestedState.membership_status ||
        nestedState.subscriptionStatus ||
        nestedState.subscription_status,
    );
    const autoRenew = getOptionalBoolean(
      nestedState.autoRenew,
      nestedState.auto_renew,
      nestedState.autoRenewal,
      nestedState.auto_renewal,
    );
    const autoRenewSpecified =
      typeof state.autoRenewSpecified === 'boolean'
        ? state.autoRenewSpecified
        : autoRenew !== null;
    const explicitCancelAtPeriodEnd = getOptionalBoolean(
      nestedState.cancelAtPeriodEnd,
      nestedState.cancel_at_period_end,
      nestedState.cancelledAtPeriodEnd,
      nestedState.cancelled_at_period_end,
      nestedState.willCancelAtPeriodEnd,
      nestedState.will_cancel_at_period_end,
    );
    return {
      plan: String(
        nestedState.plan ||
          nestedState.plan_type ||
          nestedState.tier ||
          'BASIC',
      ).toUpperCase(),
      status,
      billingStatus: String(nestedState.billingStatus || '').toUpperCase(),
      paidThrough:
        nestedState.paidThrough ||
        nestedState.currentPeriodEndsAt ||
        nestedState.periodEndAt ||
        nestedState.period_end_at ||
        nestedState.paid_through ||
        '',
      billingCycle: nestedState.billingCycle || nestedState.billing_cycle || '',
      membershipSource:
        String(
          nestedState.membershipSource ||
            nestedState.membership_source ||
            'PAID',
        ).toUpperCase() === 'GIFT'
          ? 'GIFT'
          : 'PAID',
      giftId: nestedState.giftId || nestedState.gift_id || null,
      giftEndsAt: nestedState.giftEndsAt || nestedState.gift_ends_at || null,
      resumeVisible: getOptionalBoolean(
        nestedState.resumeVisible,
        nestedState.resume_visible,
      ),
      renewalSignupId:
        nestedState.renewalSignupId || nestedState.renewal_signup_id || null,
      renewalStatus:
        nestedState.renewalStatus || nestedState.renewal_status || null,
      renewalPlan: nestedState.renewalPlan || nestedState.renewal_plan || null,
      renewalBillingCycle:
        nestedState.renewalBillingCycle ||
        nestedState.renewal_billing_cycle ||
        null,
      renewalScheduledAt:
        nestedState.renewalScheduledAt ||
        nestedState.renewal_scheduled_at ||
        null,
      autoRenew: autoRenew === true,
      autoRenewSpecified,
      cancelAtPeriodEnd:
        status === 'CANCELLING' ||
        explicitCancelAtPeriodEnd === true ||
        (autoRenewSpecified && autoRenew === false),
      interop:
        state && state.interop && typeof state.interop === 'object'
          ? state.interop
          : nestedState &&
              nestedState.interop &&
              typeof nestedState.interop === 'object'
            ? nestedState.interop
            : null,
    };
  }

  function requiresPaymentMethodUpdate(state) {
    const membership = normalizeMembershipState(state);
    return (
      membership.status === 'DUNNING' ||
      (membership.billingStatus === 'UNPAID' &&
        ['ACTIVE', 'CANCELLING'].includes(membership.status))
    );
  }

  function mapMembershipState(state) {
    const membershipState = normalizeMembershipState(state);
    const planType = toPlanType(membershipState.plan);
    const hasPaidPlan = planType !== 'basic';
    const hasActiveEntitlement =
      hasPaidPlan &&
      membershipState.membershipSource !== 'GIFT' &&
      (membershipState.status === 'ACTIVE' ||
        ((membershipState.status === 'CANCELLING' ||
          membershipState.status === 'DUNNING') &&
          isFutureTime(membershipState.paidThrough)));
    const hasActiveGift =
      membershipState.membershipSource === 'GIFT' &&
      membershipState.status === 'ACTIVE' &&
      (membershipState.giftEndsAt === null ||
        isFutureTime(membershipState.giftEndsAt));
    const giftResumeVisible =
      hasActiveGift &&
      !!membershipState.giftEndsAt &&
      membershipState.resumeVisible !== false &&
      !hasOpenGiftSignup(membershipState);
    const isCancelling =
      hasActiveEntitlement && membershipState.cancelAtPeriodEnd;
    return {
      plan_type: hasActiveEntitlement || hasActiveGift ? planType : 'basic',
      membership_status: membershipState.status,
      billing_status: membershipState.billingStatus,
      paid_through: membershipState.paidThrough,
      auto_renew: membershipState.autoRenew,
      cancel_at_period_end: isCancelling,
      billing_cycle: hasActiveEntitlement
        ? normalizeBillingCycle(membershipState.billingCycle, planType)
        : 'free',
      membership_source: membershipState.membershipSource,
      gift_id: membershipState.giftId,
      gift_ends_at: membershipState.giftEndsAt,
      resume_visible: giftResumeVisible,
      renewal_status: membershipState.renewalStatus,
      renewal_scheduled_at: membershipState.renewalScheduledAt,
      renewal_plan: membershipState.renewalPlan,
      renewal_billing_cycle: membershipState.renewalBillingCycle,
      vip_subscription_status: giftResumeVisible
        ? 'resume'
        : isCancelling
          ? 'cancel'
          : hasActiveEntitlement
            ? 'active'
            : '',
      interop: membershipState.interop || null,
    };
  }

  function mapMembershipSummary(summary) {
    return mapMembershipState({
      ...summary,
      plan: summary && (summary.plan || summary.plan_type),
      status:
        summary &&
        (summary.status ||
          summary.membershipStatus ||
          summary.membership_status),
      paidThrough:
        summary &&
        (summary.paidThrough ||
          summary.currentPeriodEndsAt ||
          summary.periodEndAt ||
          summary.period_end_at ||
          summary.paid_through),
      billingCycle: summary && (summary.billingCycle || summary.billing_cycle),
      billingStatus:
        summary && (summary.billingStatus || summary.billing_status),
      autoRenew:
        summary &&
        (summary.autoRenew !== undefined
          ? summary.autoRenew
          : summary.auto_renew),
      cancelAtPeriodEnd:
        summary &&
        (summary.cancelAtPeriodEnd !== undefined
          ? summary.cancelAtPeriodEnd
          : summary.cancel_at_period_end),
    });
  }

  function hasOpenGiftSignup(state) {
    return [
      'PAYMENT_SOURCE_READY',
      'SETUP_PENDING',
      'SUBSCRIPTION_SCHEDULED',
      'SCHEDULED',
    ].includes(state.renewalStatus || state.renewal_status);
  }

  function canCancelMembershipPlan(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    if (hasOpenGiftSignup(state)) return true;
    if (state.membershipSource === 'GIFT') return false;
    const autoRenewAllowsCancellation =
      !state.autoRenewSpecified || state.autoRenew === true;
    return (
      (plan === 'PRO' || plan === 'PREMIUM') &&
      state.status === 'ACTIVE' &&
      autoRenewAllowsCancellation &&
      state.cancelAtPeriodEnd === false
    );
  }

  function getThemeMembershipState() {
    if (window.theme && window.theme.membershipState) {
      return window.theme.membershipState;
    }
    if (window.JJB_CUSTOMER_MEMBERSHIP_STATE) {
      return window.JJB_CUSTOMER_MEMBERSHIP_STATE;
    }
    return {
      plan:
        (window.theme && window.theme.membershipPlan) ||
        window.JJB_CUSTOMER_MEMBERSHIP_PLAN ||
        'BASIC',
      status:
        (window.theme && window.theme.membershipStatus) ||
        window.JJB_CUSTOMER_MEMBERSHIP_STATUS ||
        'BASIC',
      paidThrough:
        (window.theme && window.theme.membershipPaidThrough) ||
        window.JJB_CUSTOMER_MEMBERSHIP_PAID_THROUGH ||
        '',
      billingCycle:
        (window.theme && window.theme.membershipBillingCycle) ||
        window.JJB_CUSTOMER_MEMBERSHIP_BILLING_CYCLE ||
        '',
      autoRenew:
        (window.theme && window.theme.membershipAutoRenew) ??
        window.JJB_CUSTOMER_MEMBERSHIP_AUTO_RENEW ??
        false,
      membershipSource:
        window.theme?.membershipSource ||
        window.JJB_CUSTOMER_MEMBERSHIP_SOURCE ||
        'PAID',
      giftId: window.theme?.giftId || window.JJB_CUSTOMER_GIFT_ID || null,
      giftEndsAt:
        window.theme?.giftEndsAt || window.JJB_CUSTOMER_GIFT_ENDS_AT || null,
      resumeVisible:
        window.theme?.resumeVisible === true ||
        window.JJB_CUSTOMER_RESUME_VISIBLE === true,
      cancelAtPeriodEnd:
        (window.theme && window.theme.membershipCancelAtPeriodEnd) ??
        window.JJB_CUSTOMER_MEMBERSHIP_CANCEL_AT_PERIOD_END ??
        false,
    };
  }

  function getCurrentMembership() {
    return mapMembershipState(getThemeMembershipState());
  }

  function isCustomerLoggedIn() {
    const customerId = window.theme && window.theme.customerId;
    return customerId !== undefined && customerId !== null && customerId !== '';
  }

  function bindMembershipProfile(root) {
    if (root.dataset.jjbMembershipProfileBound === 'true') return;
    root.dataset.jjbMembershipProfileBound = 'true';
    root
      .querySelectorAll('[data-membership-profile-open]')
      .forEach((button) => {
        button.addEventListener('click', () => {
          window.jjbMembershipProfilePopup?.open({
            firstName: String(window.theme?.customerFirstName || '').trim(),
            lastName: String(window.theme?.customerLastName || '').trim(),
            email: String(window.theme?.customerEmail || '').trim(),
          });
        });
      });
  }

  function getAuthUrl(returnUrl) {
    let target;
    try {
      const url = new URL(
        returnUrl || window.location.href,
        window.location.origin,
      );
      target =
        url.origin === window.location.origin
          ? url.pathname + url.search
          : window.location.pathname + window.location.search;
    } catch (_) {
      target = window.location.pathname + window.location.search;
    }
    return (
      '/customer_authentication/login?return_to=' + encodeURIComponent(target)
    );
  }

  function toCheckoutPlanParam(planType) {
    return normalizePlanType(planType) === 'prime' ? 'PREMIUM' : 'PRO';
  }

  function getCheckoutIntentUrl(planType, cycle) {
    const url = new URL(window.location.href);
    url.searchParams.set('plan', toCheckoutPlanParam(planType));
    url.searchParams.set('billingCycle', toBillingCycle(cycle));
    return url.href;
  }

  function redirectToAuth(planType, cycle) {
    const returnUrl = planType
      ? getCheckoutIntentUrl(planType, cycle)
      : window.location.href;
    window.location.href = getAuthUrl(returnUrl);
  }

  function redirectToAuthAfterPaint(planType, cycle) {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => redirectToAuth(planType, cycle));
    });
  }

  function normalizePlanType(planType) {
    const value = String(planType || 'basic').toLowerCase();
    return PLAN_ORDER[value] === undefined ? 'basic' : value;
  }

  function getCheckoutIntent(params) {
    const plan = String(params.get('plan') || '').toUpperCase();
    const billingCycle = String(params.get('billingCycle') || '').toUpperCase();
    if (plan !== 'PRO' && plan !== 'PREMIUM') return null;
    if (billingCycle !== 'MONTH' && billingCycle !== 'YEAR') return null;
    return {
      planType: plan === 'PREMIUM' ? 'prime' : 'pro',
      cycle: billingCycle === 'YEAR' ? 'yearly' : 'monthly',
    };
  }

  function clearCheckoutIntentParams() {
    const url = new URL(window.location.href);
    url.searchParams.delete('plan');
    url.searchParams.delete('billingCycle');
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash,
    );
  }

  function setPaywallContentBlocked(root, blocked) {
    const content = root.querySelector('.jjb-membership-paywall__inner');
    if (!content) return;
    content.inert = blocked;
    if (blocked) content.setAttribute('aria-hidden', 'true');
    else content.removeAttribute('aria-hidden');
  }

  function setPageScrollLocked(owner, locked) {
    const html = document.documentElement;
    const body = document.body;
    if (!html || !body) return;

    if (locked) {
      if (pageScrollLockOwners.has(owner)) return;
      pageScrollLockOwners.add(owner);
      if (pageScrollLockOwners.size > 1) return;

      const scrollY = window.scrollY || html.scrollTop || body.scrollTop || 0;
      pageScrollLockSnapshot = {
        scrollY,
        htmlOverflow: html.style.overflow,
        htmlOverscrollBehavior: html.style.overscrollBehavior,
        bodyLeft: body.style.left,
        bodyOverflow: body.style.overflow,
        bodyPosition: body.style.position,
        bodyRight: body.style.right,
        bodyTop: body.style.top,
        bodyWidth: body.style.width,
      };
      html.style.overflow = 'hidden';
      html.style.overscrollBehavior = 'none';
      body.style.left = '0';
      body.style.overflow = 'hidden';
      body.style.position = 'fixed';
      body.style.right = '0';
      body.style.top = '-' + scrollY + 'px';
      body.style.width = '100%';
      return;
    }

    pageScrollLockOwners.delete(owner);
    if (pageScrollLockOwners.size > 0 || !pageScrollLockSnapshot) return;

    const snapshot = pageScrollLockSnapshot;
    pageScrollLockSnapshot = null;
    html.style.overflow = snapshot.htmlOverflow;
    html.style.overscrollBehavior = snapshot.htmlOverscrollBehavior;
    body.style.left = snapshot.bodyLeft;
    body.style.overflow = snapshot.bodyOverflow;
    body.style.position = snapshot.bodyPosition;
    body.style.right = snapshot.bodyRight;
    body.style.top = snapshot.bodyTop;
    body.style.width = snapshot.bodyWidth;
    window.scrollTo(0, snapshot.scrollY);
  }

  function bindComingSoonOverlay(root) {
    const button = root.querySelector(
      '[data-jjb-membership-continue-checkout]',
    );
    if (!button || button.dataset.jjbMembershipContinueBound === 'true') return;
    button.dataset.jjbMembershipContinueBound = 'true';
    button.addEventListener('click', () => {
      window.history.back();
    });
  }

  function showComingSoon(root) {
    const overlay = root.querySelector('[data-jjb-membership-coming-soon]');
    const shell = root.querySelector('[data-jjb-membership-shell]');
    if (!overlay) return;
    bindComingSoonOverlay(root);
    overlay.hidden = false;
    if (shell) shell.hidden = true;
    root.classList.add('is-coming-soon');
    setPageScrollLocked(root, true);
    setPaywallContentBlocked(root, true);
    const button = overlay.querySelector(
      '[data-jjb-membership-continue-checkout]',
    );
    if (button) button.focus({ preventScroll: true });
  }

  function showMembershipShell(root) {
    const overlay = root.querySelector('[data-jjb-membership-coming-soon]');
    const shell = root.querySelector('[data-jjb-membership-shell]');
    if (overlay) overlay.hidden = true;
    if (shell) shell.hidden = false;
    root.classList.remove('is-coming-soon');
    setPageScrollLocked(root, false);
    setPaywallContentBlocked(root, false);
  }

  const UPGRADE_ENTRY_PAGES = new Set([
    'cart',
    'cart_drawer',
    'cart_half',
    'cart_page',
    'cart_whole',
    'checkout',
    'generate',
    'generate_graduation',
    '25_generate',
  ]);

  function resolveInitialTab(params, defaultTab) {
    const explicitTab = String(params.get('tab') || '').toLowerCase();
    if (explicitTab === 'membership' || explicitTab === 'plans') {
      return explicitTab;
    }
    const entryPage = String(params.get('entry_page') || '').toLowerCase();
    if (UPGRADE_ENTRY_PAGES.has(entryPage) || params.has('plan')) {
      return 'plans';
    }
    return String(defaultTab || '').toLowerCase() === 'membership'
      ? 'membership'
      : 'plans';
  }

  function getTabUrl(href, tab) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('tab', tab === 'plans' ? 'plans' : 'membership');
    return url.pathname + url.search + url.hash;
  }

  function getMembershipRefreshUrl(href) {
    const url = new URL(href, window.location.origin);
    url.searchParams.set('tab', 'membership');
    url.searchParams.set('refresh_membership', '1');
    return url.href;
  }

  function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function formatCouponValue(value) {
    const number = toFiniteNumber(value);
    if (number === null) return '';
    return Number.isInteger(number) ? String(number) : number.toFixed(2);
  }

  function formatCouponDollars(cents) {
    const amount = toFiniteNumber(cents);
    return amount === null ? '' : '$' + formatCouponValue(amount / 100);
  }

  function normalizeCouponKind(coupon) {
    const kind = String(
      coupon.kind || coupon.couponType || coupon.coupon_type || '',
    ).toUpperCase();
    if (kind === 'FIXED' || kind === 'AMOUNT_OFF' || kind === 'FIXED_AMOUNT') {
      return 'FIXED';
    }
    if (kind === 'PERCENTAGE' || kind === 'PERCENT_OFF') {
      return 'PERCENTAGE';
    }
    return kind;
  }

  function normalizeCoupon(coupon, defaultStatus) {
    const item = coupon || {};
    const kind = normalizeCouponKind(item);
    const amountCents = toFiniteNumber(item.amountCents ?? item.amount_cents);
    const percentage = toFiniteNumber(item.percentage);
    const maxDiscountCents = toFiniteNumber(
      item.maxDiscountCents ?? item.max_discount_cents,
    );
    const expiresAt = item.expiresAt || item.expires_at || '';
    const usedAt = item.usedAt || item.used_at || '';
    const inactiveReason = String(
      item.inactiveReason || item.inactive_reason || '',
    ).toUpperCase();
    let status = String(
      item.status || defaultStatus || 'AVAILABLE',
    ).toUpperCase();
    if (usedAt || inactiveReason === 'USED') status = 'USED';
    else if (inactiveReason === 'EXPIRED') status = 'EXPIRED';
    // MS-089：共享账本占用／核销优先于本地 AVAILABLE
    const interopState = String(
      (item.interop && (item.interop.state || item.interop.ledgerState)) ||
        item.interopState ||
        item.interop_state ||
        '',
    ).toUpperCase();
    if (
      interopState === 'OCCUPIED' ||
      interopState === 'RESERVED' ||
      interopState === 'ACTIVE'
    ) {
      status = 'OCCUPIED';
    } else if (interopState === 'CONSUMED' || interopState === 'REDEEMED') {
      status = 'USED';
    }
    const isInactive = status !== 'AVAILABLE' && status !== 'ACTIVE';
    const generatedTitle =
      kind === 'FIXED' && amountCents !== null
        ? formatCouponDollars(amountCents)
        : kind === 'PERCENTAGE' && percentage !== null
          ? formatCouponValue(percentage) + '% OFF'
          : '';
    const generatedBadge =
      kind === 'FIXED'
        ? 'No minimum'
        : kind === 'PERCENTAGE' && maxDiscountCents !== null
          ? 'Up to ' + formatCouponDollars(maxDiscountCents)
          : '';
    const title = String(
      generatedTitle ||
        item.title ||
        item.discountLabel ||
        item.code ||
        'Member coupon',
    );
    const badge = String(
      generatedBadge ||
        item.badge ||
        item.minimumLabel ||
        item.minimum_label ||
        item.usageLimitLabel ||
        '',
    );
    const meta =
      status === 'USED'
        ? 'Used'
        : expiresAt
          ? 'Exp: ' + formatMembershipDate(expiresAt)
          : String(item.inactive_reason || 'Member offer');
    return {
      id: item.id || item.code || '',
      kind,
      amountCents,
      percentage,
      maxDiscountCents,
      status,
      expiresAt,
      usedAt,
      title,
      badge,
      meta,
      isInactive,
    };
  }

  function sortAvailableCoupons(coupons) {
    const kindOrder = { FIXED: 0, PERCENTAGE: 1 };
    return coupons.sort((left, right) => {
      const kindDifference =
        (kindOrder[left.kind] ?? 2) - (kindOrder[right.kind] ?? 2);
      if (kindDifference) return kindDifference;
      if (left.kind === 'FIXED') {
        return (right.amountCents ?? -1) - (left.amountCents ?? -1);
      }
      if (left.kind === 'PERCENTAGE') {
        return (right.percentage ?? -1) - (left.percentage ?? -1);
      }
      return 0;
    });
  }

  function normalizeMembershipCoupons(coupons) {
    const available = [];
    const inactive = [];
    const append = (items, defaultStatus) => {
      if (!Array.isArray(items)) return;
      items.forEach((coupon) => {
        const normalized = normalizeCoupon(coupon, defaultStatus);
        (normalized.isInactive ? inactive : available).push(normalized);
      });
    };
    if (Array.isArray(coupons)) {
      append(coupons, 'AVAILABLE');
    } else {
      append(coupons?.available, 'AVAILABLE');
      append(coupons?.inactive, 'EXPIRED');
    }
    return { available: sortAvailableCoupons(available), inactive };
  }

  function getGenerationQuotaProgress(usedValue, limitValue) {
    if (
      usedValue === null ||
      usedValue === undefined ||
      limitValue === null ||
      limitValue === undefined
    ) {
      return null;
    }
    const used = Number(usedValue);
    const limit = Number(limitValue);
    if (!Number.isFinite(used) || !Number.isFinite(limit) || limit <= 0) {
      return null;
    }
    const normalizedUsed = Math.max(0, used);
    const remaining = Math.max(0, limit - normalizedUsed);
    return {
      used: normalizedUsed,
      limit,
      remaining,
      percentage: Math.min(100, (normalizedUsed / limit) * 100),
    };
  }

  function formatBillingCycle(cycle) {
    const value = String(cycle || '').toUpperCase();
    if (value === 'YEAR' || value === 'YEARLY') return 'Yearly';
    if (value === 'MONTH' || value === 'MONTHLY') return 'Monthly';
    return '';
  }

  function formatBillingPlan(plan) {
    const value = String(plan || '').toUpperCase();
    if (value === 'PREMIUM' || value === 'PRIME') return 'Premium';
    if (value === 'PRO') return 'Pro';
    return '';
  }

  function formatBillingMoney(cents, currency) {
    const value = Number(cents);
    if (!Number.isFinite(value)) return '';
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: String(currency || 'USD').toUpperCase(),
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value / 100);
    } catch (_) {
      return '$' + (value / 100).toFixed(2);
    }
  }

  function formatBillingDate(value) {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }

  function normalizeBillingHistory(payload, _membership) {
    const items = Array.isArray(payload) ? payload : payload?.items;
    if (!Array.isArray(items)) return [];
    return items.map((item, index) => {
      const type = String(item?.type || 'INVOICE').toUpperCase();
      // 当前会员状态不能代表历史账单的套餐或周期。
      const plan = formatBillingPlan(item?.plan);
      const cycle = formatBillingCycle(
        item?.billingCycle || item?.billing_cycle,
      );
      let title = 'Billing Record';
      if (type === 'CREDIT_NOTE') title = 'Credit Note';
      else if (type === 'DISPUTE') title = 'Payment Dispute';
      else if (plan) title = plan + ' Plan' + (cycle ? ' - ' + cycle : '');
      const amount = formatBillingMoney(
        item?.totalCents ?? item?.total_cents,
        item?.currency,
      );
      const paymentStatus = String(
        item?.paymentStatus || item?.payment_status || '',
      ).toUpperCase();
      const statusLabel = paymentStatus
        ? paymentStatus.charAt(0) + paymentStatus.slice(1).toLowerCase()
        : '';
      return {
        id: String(item?.id || 'billing-' + index),
        title,
        amount: [amount, statusLabel].filter(Boolean).join(' '),
        createdAt: formatBillingDate(item?.createdAt || item?.created_at),
      };
    });
  }

  function formatLocalDailyResetTime(now = new Date()) {
    const beijingDateParts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(now);
    const getPart = (type) =>
      Number(beijingDateParts.find((part) => part.type === type)?.value);
    const year = getPart('year');
    const month = getPart('month');
    const day = getPart('day');
    const nextBeijingMidnight = new Date(
      Date.UTC(year, month - 1, day + 1) - 8 * 60 * 60 * 1000,
    );

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    }).format(nextBeijingMidnight);
  }

  function formatResetLabel12Hour(label) {
    const value = String(label || '');
    const match = value.match(/\b(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?\b/i);
    if (!match) return value;

    let hour = Number(match[1]);
    const minute = match[2] || '00';
    const meridiem = match[3] ? match[3].toUpperCase() : null;
    if (!Number.isFinite(hour) || hour > 23) return value;
    if (meridiem) hour = (hour % 12) + (meridiem === 'PM' ? 12 : 0);

    const displayHour = hour % 12 || 12;
    const displayMeridiem = hour >= 12 ? 'PM' : 'AM';
    return value.replace(
      match[0],
      displayHour + ':' + minute + ' ' + displayMeridiem,
    );
  }

  window.JJBMembershipPageUtils = {
    CROSS_PLATFORM_RESTRICTED_MESSAGE: CROSS_PLATFORM_RESTRICTED_MESSAGE,
    membershipInteropRestricted: membershipInteropRestricted,
    getMembershipInteropSourceLabel,
    canCancelMembershipPlan,
    getButtonState,
    getSelectablePlanTypes,
    getCancelSuccessMessage,
    getGenerationQuotaProgress,
    getMembershipAction,
    getMembershipPlanDescription,
    getMembershipRefreshUrl,
    getTabUrl,
    formatLocalDailyResetTime,
    mapMembershipState,
    normalizeBillingHistory,
    normalizeMembershipCoupons,
    normalizeMembershipState,
    resolveInitialTab,
    getMembershipCheckoutShopOrigin,
    getCheckoutSuccessUrl,
  };

  function track(eventName, params = {}, options = {}) {
    const payload = Object.assign(
      { page: window.location.pathname, send_to: window.GA_MEASUREMENT_ID },
      params || {},
    );
    try {
      return window?.zlog?.track?.(eventName, payload, {
        statsig: false,
        ...options,
      });
    } catch (_) {}
  }

  function trackMembershipEntryView(root, tab) {
    track('membership_entry_view', {
      entry_page: 'membership',
      current_plan: root.dataset.membershipPlan || 'BASIC',
      tab,
    });
  }

  function getAnalyticsEntryPage(root) {
    const entryPage = String(root.dataset.entryPage || '').toLowerCase();
    if (
      entryPage === 'generate' ||
      entryPage === 'profile' ||
      entryPage === '25_generate'
    )
      return entryPage;
    if (entryPage === 'cart_drawer' || entryPage === 'cart_half') {
      return 'cart_half';
    }
    if (
      entryPage === 'cart' ||
      entryPage === 'cart_page' ||
      entryPage === 'cart_whole'
    ) {
      return 'cart_whole';
    }
    if (entryPage === 'checkout') return 'checkout';
    return 'membership';
  }

  async function getMembershipAnalytics(root, includeEntryPage) {
    const entryPage = includeEntryPage
      ? getAnalyticsEntryPage(root)
      : undefined;
    try {
      const analytics =
        await window.jjbCheckoutRuntime?.getAnalytics?.(entryPage);
      if (analytics) return analytics;
    } catch {}
    return entryPage ? { entryPage } : {};
  }

  let popupReadyPromise;
  function waitForMembershipPopup() {
    if (window.jjbMembershipPopup || document.readyState === 'complete')
      return Promise.resolve(window.jjbMembershipPopup);
    // defer可能先于弹窗module执行；等其初始化结束后才判断是否缺少控制器。
    popupReadyPromise ||= new Promise((resolve) => {
      const ready = () => {
        document.removeEventListener('DOMContentLoaded', ready);
        window.removeEventListener('load', ready);
        resolve();
      };
      document.addEventListener('DOMContentLoaded', ready, { once: true });
      window.addEventListener('load', ready, { once: true });
    });
    return popupReadyPromise.then(() => window.jjbMembershipPopup);
  }

  async function createMembershipOperation(root, includeEntryPage) {
    return {
      idempotencyKey: getIdempotencyKey(),
      analytics: await getMembershipAnalytics(root, includeEntryPage),
    };
  }

  function formatMoney(value) {
    const number = Number(value || 0);
    if (number <= 0) return 'Free';
    return '$' + number.toFixed(2);
  }

  function formatCardMoney(value) {
    return formatMoney(value);
  }

  function getPlanDisplayPrice(planType, cycle) {
    const normalized = normalizePlanType(planType);
    const plan = PLANS.find((item) => item.plan_type === normalized);
    if (!plan) return '';
    return formatMoney(
      normalizeBillingCycle(cycle, normalized) === 'yearly'
        ? plan.yearly_first_year_price
        : plan.monthly_price,
    );
  }

  function planName(planType) {
    const normalized = normalizePlanType(planType);
    if (normalized === 'prime') return 'Premium';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  function analyticsPlanName(planType) {
    const normalized = normalizePlanType(planType);
    if (normalized === 'prime') return 'Prime';
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  }

  function canUpgradeTo(currentPlan, currentCycle, targetPlan, targetCycle) {
    const current = normalizePlanType(currentPlan);
    const target = normalizePlanType(targetPlan);
    const currentBillingCycle = normalizeBillingCycle(currentCycle, current);
    const targetBillingCycle = normalizeBillingCycle(targetCycle, target);
    if (target === 'basic') return false;
    if (current === 'basic') return true;
    if (current === 'pro' && currentBillingCycle === 'monthly') {
      return (
        (target === 'pro' && targetBillingCycle === 'yearly') ||
        target === 'prime'
      );
    }
    if (current === 'pro' && currentBillingCycle === 'yearly') {
      return target === 'prime' && targetBillingCycle === 'yearly';
    }
    if (current === 'prime' && currentBillingCycle === 'monthly') {
      return target === 'prime' && targetBillingCycle === 'yearly';
    }
    return false;
  }

  function getButtonState(plan, membership, targetCycle) {
    const target = normalizePlanType(plan.plan_type);
    const current = normalizePlanType(membership.plan_type);
    const currentCycle = normalizeBillingCycle(
      membership.billing_cycle,
      current,
    );
    const normalizedTargetCycle = normalizeBillingCycle(targetCycle, target);
    const isCurrentPlan =
      target === current &&
      (current === 'basic' || currentCycle === normalizedTargetCycle);
    const isSubscriptionCancelled =
      current !== 'basic' && membership.vip_subscription_status === 'cancel';

    if (
      target !== 'basic' &&
      (membership.membership_status === 'DUNNING' ||
        (membership.billing_status === 'UNPAID' &&
          ['ACTIVE', 'CANCELLING'].includes(membership.membership_status)))
    ) {
      return {
        label: 'Update payment method',
        disabled: false,
        action: 'payment_method_setup',
      };
    }
    if (hasOpenGiftSignup(membership)) {
      // 待生效签约（赠转付预约）：以预约档位周期为基准渲染——预约本身是
      // Current Plan，更高档位/更长周期可升级换挡（后端取消旧意向建新意向），
      // 其余 Included；非赠送来源（赠送已撤销的残留签约）或旧接口缺少预约
      // 档位时保持整体 pending。
      const pendingPlan = toPlanType(membership.renewal_plan);
      const pendingCycle = normalizeBillingCycle(
        membership.renewal_billing_cycle,
        pendingPlan,
      );
      if (membership.membership_source === 'GIFT' && pendingPlan !== 'basic') {
        if (target === pendingPlan && pendingCycle === normalizedTargetCycle) {
          return { label: 'Current Plan', disabled: true, action: 'current' };
        }
        if (
          canUpgradeTo(pendingPlan, pendingCycle, target, normalizedTargetCycle)
        ) {
          return { label: 'Upgrade', disabled: false, action: 'checkout' };
        }
        return { label: 'Included', disabled: true, action: 'included' };
      }
      return {
        label: 'Subscription pending',
        disabled: true,
        action: 'pending',
      };
    }
    if (membership.membership_source === 'GIFT' && current !== 'basic') {
      if (target !== 'basic' && membership.resume_visible) {
        // 赠转付：向更高档位转付是升级转化显示 Upgrade；同档位/降档转付显示 Resume。
        return {
          label:
            PLAN_ORDER[target] > PLAN_ORDER[current] ? 'Upgrade' : 'Resume',
          disabled: false,
          action: 'checkout',
        };
      }
      return {
        label: target === current ? 'Current Plan' : 'Included',
        disabled: true,
        action: 'current',
      };
    }
    if (isCurrentPlan && isSubscriptionCancelled) {
      return { label: 'Resume', disabled: false, action: 'resume' };
    }
    if (isCurrentPlan) {
      return { label: 'Current Plan', disabled: true, action: 'current' };
    }
    if (!canUpgradeTo(current, currentCycle, target, normalizedTargetCycle)) {
      return { label: 'Included', disabled: true, action: 'included' };
    }
    return {
      label: target === 'pro' ? 'Get Pro' : 'Get Premium',
      disabled: false,
      action: 'checkout',
    };
  }

  function getMembershipPopupCheckoutPayload(root, button, membership) {
    const planType = normalizePlanType(button.dataset.planType);
    const billingCycle = normalizeBillingCycle(
      root.dataset.billingCycle,
      planType,
    );
    const isUpgrade =
      normalizePlanType(membership && membership.plan_type) !== 'basic';
    return {
      entry_page: isUpgrade ? 'membership' : 'direct',
      plan_type: planType === 'prime' ? 'premium' : planType,
      billing_cycle: billingCycle,
      plan_label: planName(planType) + ' Membership',
      display_price: getPlanDisplayPrice(planType, billingCycle),
      action_type: isUpgrade ? 'upgrade' : 'purchase',
    };
  }

  function getCheckoutSource(root) {
    return root.dataset.returnUrl || window.location.href;
  }

  function getMembershipCheckoutShopOrigin() {
    const origin = String(window.location?.origin || '');
    if (origin === 'https://jujubit.ai') {
      return 'https://exn11x-id.myshopify.com';
    }
    if (origin === 'https://jujubit-dev.myshopify.com') {
      return 'https://jujubit-dev.myshopify.com';
    }
    return origin;
  }

  function createCheckoutSourceHash() {
    if (typeof window.crypto?.randomUUID === 'function') {
      return 'h_' + window.crypto.randomUUID();
    }
    return 'h_' + Date.now().toString(36) + Math.random().toString(36).slice(2);
  }

  function storeCheckoutSource(source) {
    const hash = createCheckoutSourceHash();
    try {
      window.localStorage.setItem(
        CHECKOUT_SOURCE_STORAGE_KEY_PREFIX + hash,
        source,
      );
    } catch (_) {}
    return hash;
  }

  function getCheckoutSuccessUrl(root, planType) {
    const successUrl = new URL(
      '/pages/checkout-succeed',
      getMembershipCheckoutShopOrigin(),
    );
    successUrl.searchParams.set(
      'source',
      storeCheckoutSource(getCheckoutSource(root)),
    );
    successUrl.searchParams.set(
      'plan',
      planType === 'prime' ? 'PREMIUM' : 'PRO',
    );
    return successUrl.href;
  }

  async function createMembershipCheckout(root, planType, cycle, operation) {
    return requestMembershipApi('/checkouts', {
      method: 'POST',
      body: {
        idempotencyKey: operation.idempotencyKey,
        plan: planType === 'prime' ? 'PREMIUM' : 'PRO',
        billingCycle: toBillingCycle(cycle),
        successUrl: operation.successUrl,
        // 会员收银台只保留 HOSTED 跳转；显式传值兼容旧后端。
        uiMode: 'HOSTED',
        analytics: operation.analytics,
      },
    });
  }

  async function resumeSubscription(operation) {
    return requestMembershipApi('/subscription/resume', {
      method: 'POST',
      body: Object.assign(
        { idempotencyKey: operation.idempotencyKey },
        Object.keys(operation.analytics).length
          ? { analytics: operation.analytics }
          : {},
      ),
    });
  }

  async function cancelSubscription(idempotencyKey) {
    return requestMembershipApi('/subscription/cancel', {
      method: 'POST',
      body: { idempotencyKey },
    });
  }

  async function redirectToCheckout(checkout, { action, planType, cycle }) {
    const url = String(checkout?.url || '');
    if (!/^https?:\/\//i.test(url)) {
      throw new Error('Checkout is not ready. Please try again.');
    }
    await track('membership_checkout_redirect', {
      customerId: window.theme?.customerId || null,
      checkoutId: checkout?.id || checkout?.checkoutId || null,
      action,
      plan:
        planType === 'prime' ? 'PREMIUM' : String(planType || '').toUpperCase(),
      billingCycle: toBillingCycle(cycle),
    });
    const checkoutId = checkout?.id || checkout?.checkoutId;
    if (checkoutId) {
      try {
        window.sessionStorage.setItem(CHECKOUT_ID_STORAGE_KEY, checkoutId);
      } catch (_) {}
    }
    window.zlog?.flushTracking?.();
    window.location.href = url;
  }

  async function beginCheckout(root, planType, cycle, membership) {
    // 有效会员来源在另一端时，购买/升级/换卡入口只弹固定文案，不打开收银台（MS-084）
    var restrictedReason = membershipInteropRestricted(membership);
    if (restrictedReason) {
      showCrossPlatformRestrictedDialog(root, restrictedReason);
      return;
    }
    const current = normalizePlanType(membership && membership.plan_type);
    const giftResume =
      membership?.membership_source === 'GIFT' &&
      (membership?.resume_visible || hasOpenGiftSignup(membership));
    // 共用同一控制器，登录回跳、套餐页和弹窗不会各自再创建一次会话。
    const popup = await waitForMembershipPopup();
    if (popup?.openCheckout) {
      // 会员收银台只保留 HOSTED：等待本轮 checkout 启动完成（已触发跳转或已展示错误），
      // 让 GET PRO 等入口按钮的 loading 保持到流程结束而不是闪一下即被重置。
      await popup.openCheckout({
        entry_page: getAnalyticsEntryPage(root),
        plan_type: planType === 'prime' ? 'premium' : 'pro',
        billing_cycle: cycle,
        plan_label:
          planType === 'prime' ? 'Premium Membership' : 'Pro Membership',
        display_price: getPlanDisplayPrice(planType, cycle),
        action_type: requiresPaymentMethodUpdate({
          status: membership?.membership_status,
          billingStatus: membership?.billing_status,
        })
          ? 'payment_method_setup'
          : current === 'basic'
            ? 'purchase'
            : 'upgrade',
        giftId: membership?.gift_id,
        purpose: giftResume
          ? 'GIFT_RESUME'
          : requiresPaymentMethodUpdate({
                status: membership?.membership_status,
                billingStatus: membership?.billing_status,
              })
            ? 'SETUP'
            : 'PURCHASE',
      });
      return;
    }
    const latestMembership = await requestMembershipApi('/me');
    updateMembershipOverview(root, latestMembership);
    if (requiresPaymentMethodUpdate(latestMembership)) {
      throw new Error(
        'Please open the membership page to update your payment method.',
      );
    }
    if (
      normalizeMembershipState(latestMembership).membershipSource === 'GIFT' ||
      hasOpenGiftSignup(latestMembership)
    ) {
      throw new Error(
        'Please reopen the membership page to resume your gift membership.',
      );
    }
    // 未渲染弹窗section的模板仍可以通过Hosted完成购买。
    const runtime = window.jjbCheckoutRuntime;
    const attempt = runtime?.createAttempt?.('HOSTED', 'missing_popup');
    const measure = (phase, operation) =>
      attempt ? attempt.measure(phase, operation) : operation();
    try {
      const analytics = await measure('analytics', () =>
        getMembershipAnalytics(root, true),
      );
      const intent = runtime?.getIntent?.({
        plan: planType === 'prime' ? 'PREMIUM' : 'PRO',
        billingCycle: toBillingCycle(cycle),
        uiMode: 'HOSTED',
        environment: isTestMode() ? 'test' : 'prod',
        analytics,
        createSuccessUrl: () => getCheckoutSuccessUrl(root, planType),
      }) || {
        idempotencyKey: getIdempotencyKey(),
        successUrl: getCheckoutSuccessUrl(root, planType),
        analytics,
      };
      const checkout = await measure('backend', () =>
        createMembershipCheckout(root, planType, cycle, {
          ...intent,
          uiMode: 'HOSTED',
        }),
      );
      if (
        [
          'COMPLETED',
          'APPLIED',
          'CANCELLED',
          'EXPIRED',
          'FAILED',
          'REFUNDED',
        ].includes(checkout?.status)
      ) {
        runtime?.retireIntent?.(intent.idempotencyKey);
        throw new Error('Checkout has ended. Please try again.');
      }
      await attempt?.finish('redirect');
      await redirectToCheckout(checkout, {
        action: current === 'basic' ? 'buy' : 'upgrade',
        planType,
        cycle,
      });
    } catch (error) {
      attempt?.finish('error');
      throw error;
    }
  }

  function getDefaultSelectedPlanType(membership) {
    const current = normalizePlanType(membership && membership.plan_type);
    if (current === 'basic') return 'pro';
    if (current === 'pro') return 'prime';
    return 'prime';
  }

  function getSelectablePlanTypes(membership, availablePlanTypes, targetCycle) {
    const available =
      Array.isArray(availablePlanTypes) && availablePlanTypes.length
        ? availablePlanTypes
        : ['basic', 'pro', 'prime'];
    if (hasOpenGiftSignup(membership)) {
      const pendingPlan = toPlanType(membership.renewal_plan);
      const pendingCycle = normalizeBillingCycle(
        membership.renewal_billing_cycle,
        pendingPlan,
      );
      if (membership.membership_source === 'GIFT' && pendingPlan !== 'basic') {
        return available.filter((planType) =>
          canUpgradeTo(pendingPlan, pendingCycle, planType, targetCycle),
        );
      }
      return [];
    }
    if (
      membership?.membership_source === 'GIFT' &&
      membership.plan_type !== 'basic'
    ) {
      return membership.resume_visible
        ? available.filter((p) => p !== 'basic')
        : [];
    }
    return available.filter((planType) =>
      canUpgradeTo(
        membership && membership.plan_type,
        membership && membership.billing_cycle,
        planType,
        targetCycle,
      ),
    );
  }

  function resolveSelectedPlanType(
    root,
    membership,
    availablePlanTypes,
    targetCycle,
  ) {
    let selected = normalizePlanType(root && root.dataset.selectedPlan);
    const selectable = getSelectablePlanTypes(
      membership,
      availablePlanTypes,
      targetCycle,
    );
    if (selectable.indexOf(selected) !== -1) return selected;
    selected = getDefaultSelectedPlanType(membership);
    if (selectable.indexOf(selected) !== -1) return selected;
    return selectable[0] || '';
  }

  function getActivePaywallVisualRoot(root) {
    return (
      root?.querySelector(
        '[data-membership-visual-variant="' +
          (root.dataset.membershipVisualVariant || 'legacy') +
          '"]',
      ) || root
    );
  }

  function syncSelectedPlan(root, planType) {
    const selected = normalizePlanType(planType);
    const visualRoot = getActivePaywallVisualRoot(root);
    const card = visualRoot.querySelector(
      '[data-plan-card="' + selected + '"]',
    );
    if (!card || card.dataset.selectable !== 'true') return;
    root.dataset.selectedPlan = selected;
    visualRoot.querySelectorAll('[data-plan-card]').forEach((planCard) => {
      const isSelected = planCard.dataset.planCard === selected;
      planCard.classList.toggle('is-selected', isSelected);
      planCard.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });
  }

  function getPlanContent(planType) {
    const common = [
      'Up to 3 generations at a time',
      'Free digital crafting',
      'Faster generation speed',
      'Higher generation quality',
      'Priority support',
    ];
    if (planType === 'basic') {
      return {
        included: [
          '<span class="jjb-membership-card__limited-time">Limited time</span><mark>10% OFF</mark> or more on all products',
          FEATURE_DIVIDER,
          ...(basicDailyLimit === null
            ? []
            : [
                '<span class="jjb-membership-card__limited-time">Limited time</span><mark>' +
                  basicDailyLimit +
                  '</mark> free generations / day',
              ]),
          '<span class="jjb-membership-card__limited-time">Limited time</span>Up to 3 generations at a time',
          '<span class="jjb-membership-card__limited-time">Limited time</span>Free digital crafting',
          'General generation speed',
        ],
        comingSoon: [],
      };
    }
    if (planType === 'pro') {
      return {
        included: [
          '<span class="jjb-membership-card__limited-time">Limited time</span><mark>$20 OFF</mark> one order / month',
          '<mark>2 &times; 20% OFF</mark> coupons / month',
          '<mark>10% OFF</mark> or more on all products',
          FEATURE_DIVIDER,
          '<mark>100</mark> generations / day',
        ].concat(common),
        comingSoon: ['Free shipping coupons', 'Exclusive templates'],
      };
    }
    return {
      included: [
        '<span class="jjb-membership-card__limited-time">Limited time</span><mark>$50 OFF</mark> one order / month',
        '<mark>4 &times; 20% OFF</mark> coupons / month',
        '<mark>10% OFF</mark> or more on all products',
        FEATURE_DIVIDER,
        '<mark>200</mark> generations / day',
      ].concat(common),
      comingSoon: [
        'Birthday gift',
        'Free shipping coupons',
        'Exclusive templates',
      ],
    };
  }

  function renderFeatureList(items, disabled) {
    if (!items || !items.length) return '';
    return (
      '<ul class="jjb-membership-card__features' +
      (disabled ? ' is-disabled' : '') +
      '">' +
      items
        .map((item) => {
          if (item === FEATURE_DIVIDER) {
            return '<li class="jjb-membership-card__feature-divider" aria-hidden="true"></li>';
          }
          return (
            '<li>' +
            renderFeatureIcon(item, disabled) +
            '<span>' +
            item +
            '</span></li>'
          );
        })
        .join('') +
      '</ul>'
    );
  }

  function renderFeatureIcon(item, disabled) {
    if (!disabled) {
      return '<span class="jjb-membership-card__check" aria-hidden="true"></span>';
    }
    return '<svg class="jjb-membership-card__soon-icon" width="6" height="6" viewBox="0 0 6 6" fill="none" aria-hidden="true" focusable="false"><circle cx="3" cy="3" r="3" fill="#D9D9D9"/></svg>';
  }

  function renderPlanCard(
    plan,
    membership,
    cycle,
    selectedPlanType,
    selectablePlanTypes,
    sectionId,
  ) {
    const planType = normalizePlanType(plan.plan_type);
    const button = getButtonState(plan, membership, cycle);
    const sourceLabel =
      planType === normalizePlanType(membership.plan_type)
        ? getMembershipInteropSourceLabel(membership)
        : '';
    const isSelected = planType === selectedPlanType;
    const isSelectable =
      Array.isArray(selectablePlanTypes) &&
      selectablePlanTypes.indexOf(planType) !== -1;
    const priceHtml =
      cycle === 'yearly' && planType !== 'basic'
        ? '<div class="jjb-membership-card__price-stack"><p class="jjb-membership-card__price"><span>' +
          renderMembershipBebasText(
            formatCardMoney(plan.yearly_first_year_price),
          ) +
          '</span><span class="jjb-membership-card__original-price">' +
          formatMoney(plan.yearly_original_price) +
          '</span><span style="font-size:18px;font-family: \'Outfit\';margin-right: 0;">/</span><small>year</small></p><p class="jjb-membership-card__note">20% OFF your first annual plan</p></div>'
        : cycle === 'yearly' && planType === 'basic'
          ? '<div class="jjb-membership-card__price-stack jjb-membership-card__price-stack--basic-yearly"><p class="jjb-membership-card__price"><span>' +
            renderMembershipBebasText(formatCardMoney(plan.monthly_price)) +
            '</span></p><p class="jjb-membership-card__note jjb-membership-card__note--placeholder" aria-hidden="true"></p></div>'
          : '<p class="jjb-membership-card__price"><span>' +
            renderMembershipBebasText(formatCardMoney(plan.monthly_price)) +
            '</span>' +
            (planType === 'basic' ? '' : '<small>/ month</small>') +
            '</p>';
    const planContent = getPlanContent(planType);
    const headingId =
      'plan-' + (planType === 'prime' ? 'premium' : planType) + '-' + sectionId;

    return (
      '<article class="jjb-membership-card' +
      ' is-plan-' +
      planType +
      (isSelected ? ' is-selected' : '') +
      (isSelectable ? ' is-selectable' : ' is-locked') +
      '" data-plan-card="' +
      planType +
      '" data-selectable="' +
      (isSelectable ? 'true' : 'false') +
      '" tabindex="' +
      (isSelectable ? '0' : '-1') +
      '" aria-labelledby="' +
      headingId +
      '" aria-selected="' +
      (isSelected ? 'true' : 'false') +
      '">' +
      '<div class="jjb-membership-card__header"><h3 id="' +
      headingId +
      '" class="jjb-membership-card__title">' +
      renderMembershipBebasText(planName(planType)) +
      (sourceLabel
        ? '<span class="jjb-membership-card__source" data-membership-source-label>' +
          sourceLabel +
          '</span>'
        : '') +
      '</h3>' +
      (planType === 'pro'
        ? '<span class="jjb-membership-card__value-badge">' +
          renderMembershipBebasText('Most Popular') +
          '</span>'
        : '') +
      priceHtml +
      '</div>' +
      '<button type="button" class="jjb-membership-card__button" data-plan-type="' +
      planType +
      '" data-action="' +
      button.action +
      '"' +
      (button.disabled ? ' disabled' : '') +
      '>' +
      button.label +
      '</button>' +
      '<div class="jjb-membership-card__section"><p>' +
      renderMembershipBebasText('INcludes') +
      '</p>' +
      renderFeatureList(planContent.included, false) +
      '</div>' +
      (planContent.comingSoon.length
        ? '<div class="jjb-membership-card__section jjb-membership-card__section--soon"><p>' +
          renderMembershipBebasText('Coming Soon') +
          '</p>' +
          renderFeatureList(planContent.comingSoon, true) +
          '</div>'
        : '') +
      '</article>'
    );
  }

  function renderVisualFeatureIcon(type) {
    if (type === 'alarm') {
      return `<svg class="jjb-membership-visual-card__feature-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M6.9993 12.2984C9.63524 12.2984 11.7721 10.1616 11.7721 7.52568C11.7721 4.88977 9.63524 2.75293 6.9993 2.75293C4.36339 2.75293 2.22656 4.88977 2.22656 7.52568C2.22656 10.1616 4.36339 12.2984 6.9993 12.2984Z" stroke-width="1.5" stroke="currentColor"/>
        <path d="M6.93684 4.75391L6.93652 7.61985L8.9599 9.64322" stroke-width="1.5" stroke="currentColor"/>
        <path d="M1.79297 3.10049L3.61529 1.79883" stroke-width="1.5" stroke="currentColor"/>
        <path d="M12.2061 3.10036L10.3838 1.79871" stroke-width="1.5" stroke="currentColor"/>
        </svg>`;
    }
    if (type === 'close') {
      return '<svg class="jjb-membership-visual-card__feature-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="m4.2 4.2 5.6 5.6m0-5.6L4.2 9.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    }
    return '<svg class="jjb-membership-visual-card__feature-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false"><path d="m2.3 7.15 2.55 2.55 6.85-6.35" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function renderVisualLimitedTime() {
    return (
      '<span class="jjb-membership-visual-card__limited-time">' +
      renderMembershipBebasText('Limited time') +
      '</span>'
    );
  }

  function renderVisualMetric(label, value, options) {
    const settings = options || {};
    return (
      '<div class="jjb-membership-visual-card__metric' +
      (settings.disabled ? ' is-disabled' : '') +
      '"><div><span>' +
      label +
      '</span><strong>' +
      value +
      '</strong>' +
      (settings.limited ? renderVisualLimitedTime() : '') +
      '</div>' +
      (settings.progress
        ? '<span class="jjb-membership-visual-card__progress"><i style="--visual-progress:' +
          settings.progress +
          '%"></i></span>'
        : '') +
      '</div>'
    );
  }

  function renderVisualCoupon(planType, amount, count) {
    if (planType === 'basic') {
      return (
        '<div class="jjb-membership-visual-card__feature is-disabled">' +
        renderVisualFeatureIcon('close') +
        '<span>None</span></div>'
      );
    }
    return (
      '<div class="jjb-membership-visual-card__feature">' +
      renderVisualFeatureIcon('alarm') +
      '<span><mark>$' +
      amount +
      ' OFF</mark> one order / month</span>' +
      renderVisualLimitedTime() +
      '</div><div class="jjb-membership-visual-card__feature">' +
      renderVisualFeatureIcon('check') +
      '<span><mark>' +
      count +
      ' &times; 20% OFF</mark> coupons / month</span></div>'
    );
  }

  function renderVisualPerk(label, disabled, limited) {
    return (
      '<div class="jjb-membership-visual-card__feature' +
      (disabled ? ' is-disabled' : '') +
      '">' +
      renderVisualFeatureIcon(disabled ? 'close' : 'check') +
      '<span>' +
      label +
      '</span>' +
      (limited ? renderVisualLimitedTime() : '') +
      '</div>'
    );
  }

  function splitVisualPrice(text) {
    const match = /^\$(\d+)\.(\d+)$/.exec(String(text || ''));
    return match ? { integer: match[1], fraction: match[2] } : null;
  }

  // 每列 0 位为空白，1-10 对应数字 0-9；空白列宽度收起，用于位数不同的金额。
  function getVisualPriceDigitPosition(char) {
    return char === ' ' ? 0 : Number(char) + 1;
  }

  function renderVisualPriceDigit(fromChar, toChar, index) {
    const from = getVisualPriceDigitPosition(fromChar);
    const to = getVisualPriceDigitPosition(toChar);
    let track = '<span>&nbsp;</span>';
    for (let digit = 0; digit <= 9; digit += 1) {
      track += '<span>' + digit + '</span>';
    }
    return (
      '<span class="jjb-membership-visual-card__price-digit' +
      (from === 0 ? ' is-empty' : '') +
      '" data-price-digit-to="' +
      to +
      '" style="--price-digit:' +
      from +
      ';--price-digit-delay:' +
      index * 50 +
      'ms"><span class="jjb-membership-visual-card__price-track">' +
      track +
      '</span></span>'
    );
  }

  function renderVisualPriceValue(previousText, nextText) {
    const next = splitVisualPrice(nextText);
    if (!next) return nextText;
    const previous = splitVisualPrice(previousText) || next;
    const integerLength = Math.max(
      previous.integer.length,
      next.integer.length,
    );
    const fractionLength = Math.max(
      previous.fraction.length,
      next.fraction.length,
    );
    // 整数右对齐、小数左对齐，保证同一位数在两种账期间上下滚动。
    const fromInteger = previous.integer.padStart(integerLength, ' ');
    const toInteger = next.integer.padStart(integerLength, ' ');
    const fromFraction = previous.fraction.padEnd(fractionLength, ' ');
    const toFraction = next.fraction.padEnd(fractionLength, ' ');
    let index = 0;
    let digits = '';
    for (let i = 0; i < integerLength; i += 1) {
      digits += renderVisualPriceDigit(fromInteger[i], toInteger[i], index++);
    }
    digits += '<span>.</span>';
    for (let i = 0; i < fractionLength; i += 1) {
      digits += renderVisualPriceDigit(fromFraction[i], toFraction[i], index++);
    }
    return (
      '<span class="jjb-membership-visual-card__price-label">' +
      nextText +
      '</span><span class="jjb-membership-visual-card__price-roll" aria-hidden="true"><span>$</span>' +
      digits +
      '</span>'
    );
  }

  function playVisualPriceRoll(cards) {
    const digits = cards.querySelectorAll('[data-price-digit-to]');
    if (!digits.length) return;
    // 先提交旧金额位置，再切换到新位置，触发 transform 过渡。
    void cards.offsetWidth;
    digits.forEach((digit) => {
      const to = digit.dataset.priceDigitTo;
      digit.style.setProperty('--price-digit', to);
      digit.classList.toggle('is-empty', to === '0');
    });
  }

  function getInitialMobileVisualPlanType(membership) {
    const current = normalizePlanType(membership && membership.plan_type);
    if (current === 'prime') return 'prime';
    if (current === 'pro' && membership.vip_subscription_status !== 'cancel') {
      return 'prime';
    }
    return 'pro';
  }

  function showInitialMobileVisualPlan(cards, membership) {
    if (cards.dataset.mobileInitialPlanShown === 'true') return;
    if (window.innerWidth > 1200) return;
    const initialPlan = getInitialMobileVisualPlanType(membership);
    if (!cards.querySelector('[data-plan-card="' + initialPlan + '"]')) return;
    window.requestAnimationFrame(() => {
      if (cards.dataset.mobileInitialPlanShown === 'true') return;
      if (!cards.isConnected || window.innerWidth > 1200) return;
      const initialCard = cards.querySelector(
        '[data-plan-card="' + initialPlan + '"]',
      );
      if (!initialCard || !cards.clientWidth || !initialCard.offsetWidth)
        return;
      cards.scrollLeft =
        initialCard.offsetLeft -
        (cards.clientWidth - initialCard.offsetWidth) / 2;
      cards.dataset.mobileInitialPlanShown = 'true';
    });
  }

  function renderVisualPlanCard(
    plan,
    membership,
    cycle,
    selectedPlanType,
    selectablePlanTypes,
    sectionId,
    visualRoot,
    previousPrice,
  ) {
    const planType = normalizePlanType(plan.plan_type);
    const isBasic = planType === 'basic';
    const isPro = planType === 'pro';
    const visualName = planType === 'prime' ? 'premium' : planType;
    const sourceLabel =
      planType === normalizePlanType(membership.plan_type)
        ? getMembershipInteropSourceLabel(membership)
        : '';
    const accent = isBasic ? 'basic' : visualName;
    const button = getButtonState(plan, membership, cycle);
    const isSelected = planType === selectedPlanType;
    const isSelectable = selectablePlanTypes.indexOf(planType) !== -1;
    const imageUrl =
      visualRoot.dataset[visualName + 'PlanImage'] ||
      visualRoot.dataset.premiumPlanImage ||
      '';
    const headingId = 'visual-plan-' + visualName + '-' + sectionId;
    const monthlyPrice = formatCardMoney(plan.monthly_price);
    const yearlyPrice = formatCardMoney(plan.yearly_first_year_price / 12);
    const yearlyOriginalPrice = formatCardMoney(plan.monthly_price);
    const price = isBasic
      ? 'Free'
      : cycle === 'yearly'
        ? yearlyPrice
        : monthlyPrice;
    const generationCount = isBasic
      ? String(basicDailyLimit ?? 40)
      : isPro
        ? '100'
        : '200';
    const progress = isBasic ? '28' : isPro ? '50' : '100';

    return (
      '<article class="jjb-membership-visual-card is-' +
      accent +
      (isSelected ? ' is-selected' : '') +
      (isSelectable ? ' is-selectable' : ' is-locked') +
      '" data-plan-card="' +
      planType +
      '" data-selectable="' +
      (isSelectable ? 'true' : 'false') +
      '" tabindex="' +
      (isSelectable ? '0' : '-1') +
      '" aria-labelledby="' +
      headingId +
      '" aria-selected="' +
      (isSelected ? 'true' : 'false') +
      '"><h3 id="' +
      headingId +
      '" class="jjb-membership-visual-card__plan">' +
      renderMembershipBebasText(planName(planType)) +
      '</h3>' +
      (sourceLabel
        ? '<p class="jjb-membership-visual-card__source" data-membership-source-label>' +
          sourceLabel +
          '</p>'
        : '') +
      '<img class="jjb-membership-visual-card__image" src="' +
      imageUrl +
      '" width="220" height="260" alt="" loading="lazy">' +
      '<div class="jjb-membership-visual-card__header"><p class="jjb-membership-visual-card__price" data-visual-price-cycle="' +
      cycle +
      '"><strong class="jjb-membership-visual-card__price-value" data-visual-price="' +
      price +
      '">' +
      renderMembershipBebasText(renderVisualPriceValue(previousPrice, price)) +
      '</strong>' +
      (isBasic
        ? ''
        : (cycle === 'yearly'
            ? '<span class="jjb-membership-visual-card__original-price">' +
              renderMembershipBebasText(yearlyOriginalPrice) +
              '</span>'
            : '') +
          '<small>' +
          renderMembershipBebasText('/ MONTH') +
          '</small>' +
          (cycle === 'yearly'
            ? '<span class="jjb-membership-visual-card__annual-badge">20% OFF for your 1st annual plan</span>'
            : '')) +
      '</p><div class="jjb-membership-visual-card__action">' +
      (isPro
        ? '<span class="jjb-membership-visual-card__popular">' +
          renderMembershipBebasText('Most Popular') +
          '</span>'
        : '') +
      '<button type="button" class="jjb-membership-visual-card__button" data-plan-type="' +
      planType +
      '" data-action="' +
      button.action +
      '"' +
      (button.disabled ? ' disabled' : '') +
      '>' +
      button.label +
      '</button></div></div>' +
      '<div class="jjb-membership-visual-card__body"><section class="jjb-membership-visual-card__section"><div class="jjb-membership-visual-card__section-title"><h4>' +
      renderMembershipBebasText('Monthly Coupons') +
      '</h4>' +
      (isPro
        ? '<span>' +
          renderMembershipBebasText('Pays for itself in 1 order') +
          '</span>'
        : '') +
      '</div>' +
      renderVisualCoupon(planType, isPro ? '20' : '50', isPro ? '2' : '4') +
      '</section><section class="jjb-membership-visual-card__section"><h4>' +
      renderMembershipBebasText('Generation') +
      '</h4>' +
      renderVisualMetric('Generations / day', generationCount, {
        progress,
        limited: isBasic,
      }) +
      renderVisualMetric('Generations (at a time)', '3', {
        limited: isBasic,
      }) +
      renderVisualMetric('Generation speed', isBasic ? 'General' : 'Fast', {
        disabled: isBasic,
      }) +
      renderVisualMetric('Generation quality', isBasic ? 'General' : 'High', {
        disabled: isBasic,
      }) +
      '</section><section class="jjb-membership-visual-card__section"><h4>' +
      renderMembershipBebasText('Member Perks') +
      '</h4>' +
      renderVisualPerk('Free digital crafting', false, isBasic) +
      renderVisualPerk('Priority support', isBasic, false) +
      '</section></div></article>'
    );
  }

  function renderPaywall(root) {
    if (!root) return;
    const visualRoot = getActivePaywallVisualRoot(root);
    const state = paywallStates.get(root) || {
      membership: getCurrentMembership(),
      plans: PLANS,
    };
    paywallStates.set(root, state);
    const cycle = root.dataset.billingCycle || 'monthly';
    const cards = visualRoot.querySelector('[data-jjb-membership-cards]');
    const membership = state.membership;
    const plans = state.plans;
    root.classList.toggle('is-yearly', cycle === 'yearly');

    if (cards) {
      const visiblePlans = plans;
      const availablePlanTypes = visiblePlans.map((plan) =>
        normalizePlanType(plan.plan_type),
      );
      const selectedPlanType = resolveSelectedPlanType(
        root,
        membership,
        availablePlanTypes,
        cycle,
      );
      const selectablePlanTypes = getSelectablePlanTypes(
        membership,
        availablePlanTypes,
        cycle,
      );
      root.dataset.selectedPlan = selectedPlanType;
      const isVisual = root.dataset.membershipVisualVariant === 'visual';
      const previousPrices = {};
      if (isVisual) {
        cards.querySelectorAll('[data-plan-card]').forEach((card) => {
          const value = card.querySelector('[data-visual-price]');
          if (value) {
            previousPrices[card.dataset.planCard] = value.dataset.visualPrice;
          }
        });
      }
      cards.innerHTML = visiblePlans
        .map((plan) =>
          isVisual
            ? renderVisualPlanCard(
                plan,
                membership,
                cycle,
                selectedPlanType,
                selectablePlanTypes,
                root.id + '-visual',
                visualRoot,
                previousPrices[normalizePlanType(plan.plan_type)],
              )
            : renderPlanCard(
                plan,
                membership,
                cycle,
                selectedPlanType,
                selectablePlanTypes,
                root.id + '-legacy',
              ),
        )
        .join('');
      if (isVisual) {
        playVisualPriceRoll(cards);
        showInitialMobileVisualPlan(cards, membership);
      }
    }

    visualRoot.querySelectorAll('[data-billing-cycle]').forEach((control) => {
      const isActive = control.dataset.billingCycle === cycle;
      control.classList.toggle('is-active', isActive);
      control.setAttribute('aria-selected', isActive ? 'true' : 'false');
      control.setAttribute('tabindex', isActive ? '0' : '-1');
    });
    visualRoot.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.addEventListener('click', () => {
        syncSelectedPlan(root, card.dataset.planCard);
      });
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        if (event.target !== card) return;
        event.preventDefault();
        syncSelectedPlan(root, card.dataset.planCard);
      });
    });
    visualRoot.querySelectorAll('[data-plan-type]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.stopPropagation();
        syncSelectedPlan(root, button.dataset.planType);
        const action = button.dataset.action;
        if (action === 'checkout' || action === 'resume') {
          track(
            'membership_plan_click',
            {
              plan_type: analyticsPlanName(button.dataset.planType),
              billing_cycle: cycle,
              action_type:
                action === 'resume' || membership.membership_source === 'GIFT'
                  ? 'resume'
                  : membership.plan_type === 'basic'
                    ? 'purchase'
                    : 'upgrade',
              entry_page: root.dataset.entryPage || 'direct',
            },
            { statsig: true },
          );
        }
        if (action === 'payment_method_setup') {
          handleCheckout(root, button.dataset.planType, button);
        }
        if (action === 'resume') {
          handleResumeSubscription(root, button);
        }
        if (action === 'checkout')
          handleCheckout(root, button.dataset.planType, button);
      });
    });
    if (!state.impressionTracked && basicDailyLimit !== null) {
      state.impressionTracked = true;
      track(
        'membership_plan_impression',
        { entry_page: root.dataset.entryPage || 'direct' },
        { statsig: true },
      );
    }
  }

  function setPlanButtonLoading(root, button) {
    root.classList.add('is-loading');
    root.querySelectorAll('[data-plan-type]').forEach((control) => {
      control.disabled = true;
    });
    if (!button) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('aria-label', 'Loading');
    button.innerHTML =
      '<span class="jjb-membership-card__button-spinner" aria-hidden="true"></span>';
  }

  async function handleCheckout(root, planType, triggerButton) {
    const state = paywallStates.get(root);
    if (!state || state.loading) return;
    setPlanButtonLoading(root, triggerButton);
    if (!isCustomerLoggedIn()) {
      state.authRedirectPending = true;
      redirectToAuthAfterPaint(
        normalizePlanType(planType),
        root.dataset.billingCycle || 'monthly',
      );
      return;
    }
    state.loading = true;
    try {
      await beginCheckout(
        root,
        normalizePlanType(planType),
        root.dataset.billingCycle || 'monthly',
        state.membership,
      );
    } catch (error) {
      showMembershipRequestError(error);
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
      renderPaywall(root);
    }
  }

  async function handleResumeSubscription(root, triggerButton) {
    const state = paywallStates.get(root);
    if (!state || state.loading) return;
    setPlanButtonLoading(root, triggerButton);
    if (!isCustomerLoggedIn()) {
      state.authRedirectPending = true;
      redirectToAuthAfterPaint();
      return;
    }
    // 有效会员来源在另一端时，续费恢复等订阅管理只弹固定文案，不发起受限请求（MS-084）
    var resumeRestricted = membershipInteropRestricted(
      state.membership || getCurrentMembership(),
    );
    if (resumeRestricted) {
      showCrossPlatformRestrictedDialog(root, resumeRestricted);
      state.loading = false;
      root.classList.remove('is-loading');
      renderPaywall(root);
      return;
    }
    state.loading = true;
    try {
      const operation = await createMembershipOperation(root, false);
      const summary = await resumeSubscription(operation);
      const refreshed = await refreshMembershipAfterMutation(root);
      if (!refreshed) {
        if (summary) updateMembershipOverview(root, summary);
        state.membership = summary
          ? mapMembershipSummary(summary)
          : getCurrentMembership();
      }
      if (typeof window.toast === 'function') {
        window.toast('Auto-renewal resumed');
      }
    } catch (error) {
      if (typeof window.toast === 'function') {
        window.toast(getErrorMessage(error));
      }
    } finally {
      state.loading = false;
      root.classList.remove('is-loading');
      renderPaywall(root);
    }
  }

  async function handleCancelSubscription(root, button, dialog) {
    const state = paywallStates.get(root);
    if (state?.loading || root.dataset.membershipCancelLoading === 'true')
      return;
    if (!isCustomerLoggedIn()) {
      redirectToAuth();
      return;
    }
    const membership = state?.membership || getCurrentMembership();
    // 有效会员来源在另一端时，取消入口只弹固定文案（MS-084/085）
    var cancelRestricted = membershipInteropRestricted(membership);
    if (cancelRestricted) {
      showCrossPlatformRestrictedDialog(root, cancelRestricted);
      dialog?.close();
      return;
    }
    const planType = analyticsPlanName(membership.plan_type);
    track('membership_cancel_subscription_click', {
      plan_type: planType,
      membership_level: planType,
      billing_cycle: membership.billing_cycle || 'monthly',
    });
    const originalLabel = button.textContent;
    if (state) state.loading = true;
    root.dataset.membershipCancelLoading = 'true';
    root.classList.add('is-loading');
    button.disabled = true;
    button.textContent = 'Cancelling...';
    root.querySelectorAll('[data-plan-type]').forEach((control) => {
      control.disabled = true;
    });
    try {
      const membershipBeforeCancellation = getThemeMembershipState();
      const summary = await cancelSubscription(getIdempotencyKey());
      const refreshed = await refreshMembershipAfterMutation(root);
      if (!refreshed && summary) {
        updateMembershipOverview(root, summary);
        if (state) state.membership = mapMembershipSummary(summary);
      }
      dialog?.close();
      if (typeof window.toast === 'function') {
        window.toast(
          getCancelSuccessMessage(
            Object.assign({}, membershipBeforeCancellation, summary || {}),
          ),
          undefined,
          { wrap: true },
        );
      }
    } catch (error) {
      if (typeof window.toast === 'function') {
        window.toast(getErrorMessage(error));
      }
    } finally {
      if (state) state.loading = false;
      delete root.dataset.membershipCancelLoading;
      root.classList.remove('is-loading');
      button.disabled = false;
      button.textContent = originalLabel;
      if (state) renderPaywall(root);
    }
  }

  function bindCancelSubscriptionTest(root) {
    const button = root.querySelector('[data-jjb-membership-cancel-test]');
    if (!button) return;
    button.addEventListener('click', () => {
      handleCancelSubscription(root, button);
    });
  }

  function bindPaywallAccordion(root) {
    const qa = root.querySelector('[data-jjb-membership-qa]');
    if (!qa || qa.dataset.jjbMembershipAccordionBound === 'true') return;
    qa.dataset.jjbMembershipAccordionBound = 'true';
    qa.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        qa.querySelectorAll('details[open]').forEach((item) => {
          if (item !== details) item.open = false;
        });
      });
    });
  }

  function continueCheckoutIntent(root, checkoutIntent) {
    if (autoCheckoutHandled || !isCustomerLoggedIn() || !checkoutIntent) {
      return;
    }
    autoCheckoutHandled = true;
    root.dataset.billingCycle = checkoutIntent.cycle;
    root.dataset.selectedPlan = checkoutIntent.planType;
    const state = paywallStates.get(root);
    const plan = PLANS.find(
      (item) => item.plan_type === checkoutIntent.planType,
    );
    const buttonState = getButtonState(
      plan || { plan_type: checkoutIntent.planType },
      state.membership,
      checkoutIntent.cycle,
    );
    clearCheckoutIntentParams();
    renderPaywall(root);
    if (buttonState.action === 'checkout') {
      window.setTimeout(() => {
        const button = root.querySelector(
          '[data-plan-type="' + checkoutIntent.planType + '"]',
        );
        handleCheckout(root, checkoutIntent.planType, button);
      }, 0);
    } else if (typeof window.toast === 'function') {
      window.toast('The selected membership plan is no longer available.');
    }
  }

  function formatMembershipDate(value) {
    if (!value) return 'No expiry';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return new Intl.DateTimeFormat('en', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  }

  function formatMembershipPlan(plan) {
    const value = String(plan || 'BASIC').toLowerCase();
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  function updateMembershipPlanIcons(root, plan) {
    const normalizedPlan = String(plan || 'BASIC').toUpperCase();
    const iconKey =
      normalizedPlan === 'PREMIUM' || normalizedPlan === 'PRIME'
        ? 'premiumIcon'
        : normalizedPlan === 'PRO'
          ? 'proIcon'
          : 'basicIcon';
    const iconHeight = iconKey === 'basicIcon' ? 18 : 16;
    root.querySelectorAll('[data-membership-plan-icon]').forEach((icon) => {
      const source = icon.dataset[iconKey];
      if (!source) return;
      icon.src = source;
      icon.height = iconHeight;
      icon.setAttribute('height', String(iconHeight));
    });
  }

  function getCancelSuccessMessage(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    if (
      (plan !== 'PRO' && plan !== 'PREMIUM') ||
      !isFutureTime(state.paidThrough)
    ) {
      return 'Auto-renewal canceled.';
    }
    return (
      'Auto-renewal canceled. Your ' +
      formatMembershipPlan(plan) +
      ' plan stays active until ' +
      formatMembershipDate(state.paidThrough) +
      '.'
    );
  }

  function getMembershipPlanDescription(state) {
    state = normalizeMembershipState(state);
    if (requiresPaymentMethodUpdate(state)) {
      return state.plan === 'BASIC'
        ? 'Your renewal payment is overdue. Update your payment method. Paid benefits resume after payment is confirmed.'
        : 'Your renewal payment is overdue. Update your payment method to continue your subscription.';
    }
    if (hasOpenGiftSignup(state)) {
      return ['SUBSCRIPTION_SCHEDULED', 'SCHEDULED'].includes(
        state.renewalStatus,
      )
        ? 'Your subscription starts on ' +
            formatMembershipDate(state.renewalScheduledAt) +
            '.'
        : 'Your subscription setup is pending. Complete setup or cancel it before choosing another plan.';
    }
    if (state.membershipSource === 'GIFT') {
      if (mapMembershipState(state).plan_type === 'basic')
        return 'Your gift membership has ended.';
      const giftPlan = formatMembershipPlan(state.plan);
      return state.giftEndsAt
        ? `Your ${giftPlan} membership is a gift and expires on ${formatMembershipDate(state.giftEndsAt)}.`
        : `Your ${giftPlan} membership is a gift and has no expiration date.`;
    }
    if (state.plan === 'BASIC') return 'Members save $20+';
    if (state.cancelAtPeriodEnd && state.paidThrough) {
      return 'Your plan expires on ' + formatMembershipDate(state.paidThrough);
    }
    if (state.cancelAtPeriodEnd) {
      return 'Your plan will not renew.';
    }
    if (state.paidThrough) {
      return (
        'Your plan is active and renews on ' +
        formatMembershipDate(state.paidThrough) +
        '.'
      );
    }
    return 'Your membership is active.';
  }

  function getMembershipAction(membership) {
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    const summary = mapMembershipState(state);
    if (requiresPaymentMethodUpdate(state)) return 'Update payment method';
    if (state.renewalStatus === 'SETUP_PENDING' && state.renewalSignupId)
      return 'Complete setup';
    if (hasOpenGiftSignup(state)) return 'View';
    if (state.membershipSource === 'GIFT' && summary.plan_type !== 'basic')
      return summary.resume_visible ? 'Resume' : 'View';
    return summary.vip_subscription_status === 'resume' ||
      summary.vip_subscription_status === 'cancel'
      ? 'Resume'
      : plan === 'PREMIUM' && summary.plan_type !== 'basic'
        ? 'View'
        : 'Upgrade';
  }

  function publishMembershipStateUpdate(state) {
    window.dispatchEvent(
      new CustomEvent('jjb:membership-state-updated', {
        detail: {
          plan: state.plan,
          status: state.status,
          paidThrough: state.paidThrough,
          billingCycle: state.billingCycle,
          cancelAtPeriodEnd: state.cancelAtPeriodEnd,
          isLoggedIn: isCustomerLoggedIn(),
        },
      }),
    );
  }

  function updateMembershipOverview(root, membership, options) {
    if (!membership) return;
    const state = normalizeMembershipState(membership);
    const plan = state.plan === 'PRIME' ? 'PREMIUM' : state.plan;
    const summary = mapMembershipState(state);
    const action = getMembershipAction(state);
    root.dataset.membershipPlan = plan;
    root.dataset.membershipStatus = state.status;
    root.dataset.membershipPaymentUpdate = String(
      requiresPaymentMethodUpdate(state),
    );
    window.JJB_CUSTOMER_MEMBERSHIP_STATE = state;
    window.JJB_CUSTOMER_MEMBERSHIP_PLAN = plan;
    window.JJB_CUSTOMER_MEMBERSHIP_STATUS = state.status;
    window.JJB_CUSTOMER_MEMBERSHIP_PAID_THROUGH = state.paidThrough;
    window.JJB_CUSTOMER_MEMBERSHIP_BILLING_CYCLE = state.billingCycle;
    window.JJB_CUSTOMER_MEMBERSHIP_SOURCE = state.membershipSource;
    window.JJB_CUSTOMER_GIFT_ID = state.giftId;
    window.JJB_CUSTOMER_GIFT_ENDS_AT = state.giftEndsAt;
    window.JJB_CUSTOMER_RESUME_VISIBLE = state.resumeVisible;
    window.JJB_CUSTOMER_MEMBERSHIP_AUTO_RENEW = state.autoRenew;
    window.JJB_CUSTOMER_MEMBERSHIP_CANCEL_AT_PERIOD_END =
      state.cancelAtPeriodEnd;
    if (window.theme) {
      window.theme.membershipState = state;
      window.theme.membershipPlan = plan;
      window.theme.membershipStatus = state.status;
      window.theme.membershipPaidThrough = state.paidThrough;
      window.theme.membershipBillingCycle = state.billingCycle;
      window.theme.membershipSource = state.membershipSource;
      window.theme.giftId = state.giftId;
      window.theme.giftEndsAt = state.giftEndsAt;
      window.theme.resumeVisible = state.resumeVisible;
      window.theme.membershipAutoRenew = state.autoRenew;
      window.theme.membershipCancelAtPeriodEnd = state.cancelAtPeriodEnd;
    }
    if (options && options.publishMembershipState) {
      publishMembershipStateUpdate(state);
    }
    root.querySelectorAll('[data-membership-plan]').forEach((node) => {
      node.textContent = formatMembershipPlan(plan);
    });
    const sourceLabel = getMembershipInteropSourceLabel(state);
    root
      .querySelectorAll('[data-membership-overview-source-label]')
      .forEach((node) => {
        node.textContent = sourceLabel;
        node.hidden = !sourceLabel;
      });
    updateMembershipPlanIcons(root, plan);
    root
      .querySelectorAll('[data-membership-plan-description]')
      .forEach((node) => {
        node.textContent = getMembershipPlanDescription(state);
      });
    root.querySelectorAll('[data-membership-status]').forEach((node) => {
      node.textContent = state.status === 'BASIC' ? 'Free' : state.status;
    });
    root.querySelectorAll('[data-membership-paid-through]').forEach((node) => {
      node.textContent = formatMembershipDate(state.paidThrough);
    });
    root.querySelectorAll('[data-membership-billing-cycle]').forEach((node) => {
      node.textContent = state.billingCycle || 'Free';
    });
    root.querySelectorAll('[data-membership-open-plans]').forEach((node) => {
      node.textContent = action;
    });
    setMembershipCancelVisibility(root, state);
    root
      .querySelectorAll('[data-membership-cancel-plan-name]')
      .forEach((node) => {
        node.textContent = formatMembershipPlan(plan);
      });
    root
      .querySelectorAll('[data-membership-cancel-paid-through]')
      .forEach((node) => {
        node.textContent = formatMembershipDate(state.paidThrough);
      });
  }

  function setMembershipCancelVisibility(root, membership) {
    const visible = canCancelMembershipPlan(membership);
    root.querySelectorAll('[data-membership-cancel-plan]').forEach((node) => {
      node.hidden = !visible;
      node.classList.toggle('is-hidden', !visible);
      node.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  function renderCoupons(root, coupons, errorMessage) {
    const availableList = root.querySelector(
      '[data-membership-available-coupon-list]',
    );
    const expiredList = root.querySelector(
      '[data-membership-expired-coupon-list]',
    );
    if (!availableList || !expiredList) return;
    const { available, inactive } = normalizeMembershipCoupons(coupons);
    renderCouponList(
      availableList,
      available,
      errorMessage || 'No membership coupons available yet.',
    );
    renderCouponList(
      expiredList,
      inactive,
      errorMessage || 'No expired coupons yet.',
    );
  }

  function renderCouponList(list, items, emptyMessage) {
    list.replaceChildren();
    if (!items.length) {
      if (!emptyMessage) return;
      const empty = document.createElement('li');
      empty.className = 'is-empty';
      empty.textContent = emptyMessage;
      list.appendChild(empty);
      return;
    }
    items.forEach((coupon) => {
      const item = document.createElement('li');
      const title = document.createElement('strong');
      const meta = document.createElement('span');
      const badge = document.createElement('small');
      item.className = 'jjb-membership-overview__coupon-card';
      if (coupon.kind) item.classList.add('is-' + coupon.kind.toLowerCase());
      if (coupon.isInactive) item.classList.add('is-inactive');
      item.dataset.couponStatus = coupon.status;
      title.textContent = coupon.title;
      meta.textContent = coupon.meta;
      badge.textContent = coupon.badge;
      item.append(title, meta);
      if (badge.textContent) item.appendChild(badge);
      list.appendChild(item);
    });
  }

  function renderGenerationQuota(root, quota) {
    const usedNode = root.querySelector('[data-membership-generation-used]');
    const limitNode = root.querySelector('[data-membership-generation-limit]');
    const progress = root.querySelector(
      '[data-membership-generation-progress]',
    );
    const resetNode = root.querySelector('[data-membership-generation-reset]');
    if (!usedNode || !limitNode || !progress || !resetNode) return;
    const quotaProgress = getGenerationQuotaProgress(quota?.used, quota?.limit);
    const progressBar = progress.querySelector('span');
    if (!quotaProgress) {
      usedNode.textContent = '--';
      limitNode.textContent = '--';
      progress.classList.remove('is-exhausted');
      progress.classList.remove('is-empty');
      progress.setAttribute('aria-valuemax', '0');
      progress.setAttribute('aria-valuenow', '0');
      progress.setAttribute(
        'aria-valuetext',
        'Generation usage is unavailable.',
      );
      if (progressBar) progressBar.style.width = '0%';
      resetNode.textContent = 'Generation usage is unavailable.';
      return;
    }
    usedNode.textContent = quotaProgress.used.toLocaleString('en');
    limitNode.textContent = quotaProgress.limit.toLocaleString('en');
    progress.classList.toggle('is-exhausted', quotaProgress.remaining === 0);
    progress.classList.toggle('is-empty', quotaProgress.used === 0);
    progress.setAttribute('aria-valuemax', String(quotaProgress.limit));
    progress.setAttribute('aria-valuenow', String(quotaProgress.used));
    progress.setAttribute(
      'aria-valuetext',
      'Used: ' + quotaProgress.used + ' / ' + quotaProgress.limit,
    );
    if (progressBar) {
      progressBar.style.width = quotaProgress.percentage + '%';
    }
    const resetLabel =
      quota.resetLabel ||
      quota.reset_label ||
      'Resets daily at ' + formatLocalDailyResetTime();
    resetNode.textContent = formatResetLabel12Hour(resetLabel);
  }

  function renderBillingHistory(root, billing) {
    const list = root.querySelector('[data-membership-billing-list]');
    const status = root.querySelector('[data-membership-billing-status]');
    if (!list || !status) return;
    const items = normalizeBillingHistory(billing, getThemeMembershipState());
    list.replaceChildren();
    if (!items.length) {
      status.textContent = 'No billing history yet.';
      status.hidden = false;
      return;
    }
    status.hidden = true;
    items.forEach((billingItem) => {
      const item = document.createElement('li');
      const heading = document.createElement('div');
      const title = document.createElement('strong');
      const amount = document.createElement('b');
      const date = document.createElement('time');
      item.className = 'jjb-membership-overview__billing-card';
      item.dataset.billingId = billingItem.id;
      title.textContent = billingItem.title;
      amount.textContent = billingItem.amount;
      date.textContent = billingItem.createdAt;
      heading.append(title, amount);
      item.append(heading, date);
      list.appendChild(item);
    });
  }

  async function loadMembershipOverviewData(root) {
    if (!isCustomerLoggedIn()) return;
    // 每块响应独立渲染；仍等待整批结束，保留后续身份更新和Tab补读的顺序。
    const updates = await Promise.allSettled([
      requestMembershipApi('/coupons?includeExpired=true').then(
        (coupons) => renderCoupons(root, coupons),
        () => renderCoupons(root, null, 'Membership coupons are unavailable.'),
      ),
      requestMembershipApi('/quota').then(
        (quota) => renderGenerationQuota(root, quota),
        () => renderGenerationQuota(root, null),
      ),
      requestMembershipApi('/billing?pageSize=20').then(
        (billing) => renderBillingHistory(root, billing),
        () => {
          renderBillingHistory(root, { items: [] });
          const status = root.querySelector('[data-membership-billing-status]');
          if (status) {
            status.textContent = 'Billing history is unavailable.';
            status.hidden = false;
          }
        },
      ),
    ]);
    const failedUpdate = updates.find((update) => update.status === 'rejected');
    if (failedUpdate) throw failedUpdate.reason;
  }

  function syncMembershipDefaultTab(root, membership) {
    if (!membership) return;
    const defaultTab =
      mapMembershipState(membership).plan_type === 'basic'
        ? 'plans'
        : 'membership';
    // 后续无显式Tab的历史入口也需要当前身份默认值，但不因此重新切换用户正在看的页。
    root.dataset.defaultMembershipTab = defaultTab;
    const navigation = membershipDefaultNavigation.get(root);
    if (!navigation || navigation.settled) return;
    navigation.settled = true;
    if (navigation.userNavigated) return;
    const params = new URLSearchParams(window.location.search);
    const explicitTab = String(params.get('tab') || '').toLowerCase();
    if (explicitTab === 'membership' || explicitTab === 'plans') return;
    const tab = resolveInitialTab(params, defaultTab);
    const selected = root.querySelector(
      '[data-membership-tab][aria-selected="true"]',
    )?.dataset.membershipTab;
    // 本次权威读取已在加载概览，初始化切页不再额外发起一次Tab补读。
    if (selected !== tab) setMembershipTab(root, tab, false, false);
  }

  async function refreshMembership(root, clearRefreshParam) {
    if (!isCustomerLoggedIn()) return false;
    const pendingRefresh = membershipRefreshes.get(root);
    if (pendingRefresh) {
      const result = await pendingRefresh;
      if (result && clearRefreshParam) clearMembershipRefreshParam();
      return result;
    }
    const refresh = (async () => {
      try {
        const membership = await requestMembershipApi('/me');
        updateMembershipOverview(root, membership, {
          publishMembershipState: true,
        });
        syncMembershipDefaultTab(root, membership);
        await loadMembershipOverviewData(root);
        const paywallState = paywallStates.get(root);
        if (paywallState) {
          paywallState.membership = getCurrentMembership();
          renderPaywall(root);
        }
        if (clearRefreshParam) clearMembershipRefreshParam();
        return true;
      } catch (_) {
        return false;
      } finally {
        membershipRefreshes.delete(root);
      }
    })();
    membershipRefreshes.set(root, refresh);
    return refresh;
  }

  function clearMembershipRefreshParam() {
    const url = new URL(window.location.href);
    url.searchParams.delete('refresh_membership');
    window.history.replaceState(
      window.history.state,
      '',
      url.pathname + url.search + url.hash,
    );
  }

  async function refreshMembershipAfterMutation(root) {
    const pendingRefresh = membershipRefreshes.get(root);
    if (pendingRefresh) await pendingRefresh;
    return refreshMembership(root, false);
  }

  function activatePlans(root) {
    try {
      window.jjbCheckoutRuntime?.prepareAnalytics?.();
    } catch {}
    void waitForMembershipPopup().then((popup) => {
      try {
        popup?.prewarm?.();
      } catch {}
    });
    initPaywalls(root);
    const checkoutIntent = getCheckoutIntent(
      new URLSearchParams(window.location.search),
    );
    if (checkoutIntent) continueCheckoutIntent(root, checkoutIntent);
  }

  function setMembershipTab(root, tab, updateUrl, refreshOverview = true) {
    if (updateUrl) {
      const navigation = membershipDefaultNavigation.get(root);
      if (navigation) navigation.userNavigated = true;
    }
    const nextTab = tab === 'plans' ? 'plans' : 'membership';
    const previousTab = root.querySelector(
      '[data-membership-tab][aria-selected="true"]',
    )?.dataset.membershipTab;
    root.querySelectorAll('[data-membership-tab]').forEach((button) => {
      const active = button.dataset.membershipTab === nextTab;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.setAttribute('tabindex', active ? '0' : '-1');
    });
    root.querySelectorAll('[data-membership-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.membershipPanel !== nextTab;
    });
    if (nextTab === 'plans') activatePlans(root);
    if (
      nextTab === 'plans' &&
      root.dataset.membershipVisualVariant === 'visual' &&
      root.dataset.membershipVisualPlansScrolled !== 'true'
    ) {
      const scrollToBillingToggle = () => {
        const toggle = root.querySelector(
          '[data-membership-visual-variant="visual"] .jjb-membership-paywall-visual__toggle',
        );
        if (!toggle || toggle.offsetParent === null) return false;
        const rect = toggle.getBoundingClientRect();
        const topOffset = window.innerWidth <= 1200 ? 20 : 70;
        const targetTop = Math.max(0, window.scrollY + rect.top - topOffset);
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
        root.dataset.membershipVisualPlansScrolled = 'true';
        return true;
      };
      const scheduleScrollToBillingToggle = () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!scrollToBillingToggle()) {
              requestAnimationFrame(scrollToBillingToggle);
            }
          });
        });
      };
      if (document.readyState === 'complete') {
        scheduleScrollToBillingToggle();
      } else {
        window.addEventListener('load', scheduleScrollToBillingToggle, {
          once: true,
        });
      }
    }
    if (
      refreshOverview &&
      nextTab === 'membership' &&
      previousTab === 'plans'
    ) {
      // 进入管理页时可能已有旧读取在途，等待后再同步本次最新状态。
      refreshMembershipAfterMutation(root);
    }
    if (updateUrl) {
      window.history.replaceState(
        window.history.state,
        '',
        getTabUrl(window.location.href, nextTab),
      );
      track('membership_tab_click', {
        entry_page: 'membership',
        tab: nextTab,
      });
      trackMembershipEntryView(root, nextTab);
    }
  }

  function bindMembershipShell(root) {
    if (root.dataset.jjbMembershipShellBound === 'true') return;
    root.dataset.jjbMembershipShellBound = 'true';
    membershipDefaultNavigation.set(root, {
      settled: false,
      userNavigated: false,
    });
    bindMembershipProfile(root);
    root.querySelectorAll('[data-membership-tab]').forEach((button) => {
      button.addEventListener('click', () => {
        setMembershipTab(root, button.dataset.membershipTab, true);
      });
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        const tab =
          button.dataset.membershipTab === 'membership'
            ? 'plans'
            : 'membership';
        const next = root.querySelector('[data-membership-tab="' + tab + '"]');
        if (next) {
          next.focus();
          next.click();
        }
      });
    });
    root.querySelectorAll('[data-membership-open-plans]').forEach((button) => {
      button.addEventListener('click', async () => {
        if (button.disabled) return;
        const membership = normalizeMembershipState(getThemeMembershipState());
        if (
          membership.renewalStatus === 'SETUP_PENDING' &&
          membership.renewalSignupId
        ) {
          const popup = await waitForMembershipPopup();
          popup?.openCheckout?.({
            purpose: 'GIFT_RESUME',
            signupId: membership.renewalSignupId,
            scheduledStartsAt: membership.renewalScheduledAt,
          });
          return;
        }
        // 查看套餐保持即时导航；仅已明确欠费的修卡动作需要先核对最新状态。
        if (
          !isCustomerLoggedIn() ||
          !requiresPaymentMethodUpdate(getThemeMembershipState())
        ) {
          setMembershipTab(root, 'plans', true);
          return;
        }
        button.disabled = true;
        try {
          if (isCustomerLoggedIn()) {
            const latest = await requestMembershipApi('/me');
            updateMembershipOverview(root, latest);
            if (requiresPaymentMethodUpdate(latest)) {
              await beginCheckout(
                root,
                'pro',
                'monthly',
                mapMembershipState(latest),
              );
              return;
            }
          }
          setMembershipTab(root, 'plans', true);
        } catch (error) {
          showMembershipRequestError(error);
        } finally {
          button.disabled = false;
        }
      });
    });
    if (isCustomerLoggedIn()) refreshMembership(root, false);

    const cancelDialog = root.querySelector('[data-membership-cancel-dialog]');
    const cancelPlanButton = root.querySelector(
      '[data-membership-cancel-plan]',
    );
    const cancelClose = root.querySelector('[data-membership-cancel-close]');
    const cancelKeep = root.querySelector('[data-membership-cancel-keep]');
    const cancelConfirm = root.querySelector(
      '[data-membership-cancel-confirm]',
    );
    if (cancelDialog) {
      cancelDialog.addEventListener('close', () =>
        setPageScrollLocked(cancelDialog, false),
      );
    }
    if (cancelPlanButton && cancelDialog) {
      cancelPlanButton.addEventListener('click', (event) => {
        if (cancelPlanButton.disabled || cancelDialog.open) return;
        // MS-084：外平台来源在入口拦截，不打开取消确认框、不发取消请求
        var cancelRestricted = membershipInteropRestricted(
          getCurrentMembership(),
        );
        if (cancelRestricted) {
          showCrossPlatformRestrictedDialog(root, cancelRestricted);
          return;
        }
        // WebKit 鼠标点击不会自动聚焦按钮，先为原生 dialog 记录真实返回目标。
        event.currentTarget.focus({ preventScroll: true });
        setPageScrollLocked(cancelDialog, true);
        cancelDialog.showModal();
      });
    }
    if (cancelClose) {
      cancelClose.addEventListener('click', () => cancelDialog?.close());
    }
    if (cancelKeep) {
      cancelKeep.addEventListener('click', () => cancelDialog?.close());
    }
    if (cancelConfirm) {
      cancelConfirm.addEventListener('click', () =>
        handleCancelSubscription(root, cancelConfirm, cancelDialog),
      );
    }
    window.addEventListener('popstate', () => {
      const navigation = membershipDefaultNavigation.get(root);
      if (navigation) navigation.userNavigated = true;
      setMembershipTab(
        root,
        resolveInitialTab(
          new URLSearchParams(window.location.search),
          root.dataset.defaultMembershipTab,
        ),
        false,
      );
    });
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) refreshMembershipAfterMutation(root);
      else refreshMembership(root, false);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible')
        refreshMembershipAfterMutation(root);
    });
    window.addEventListener('jjb:membership-updated', () =>
      refreshMembershipAfterMutation(root),
    );
  }

  function initPaywalls(targetRoot) {
    const roots = targetRoot
      ? [targetRoot]
      : Array.from(document.querySelectorAll('[data-jjb-membership-paywall]'));
    roots.forEach((root) => {
      if (root.dataset.jjbMembershipBound === 'true') return;
      root.dataset.jjbMembershipBound = 'true';
      const params = new URLSearchParams(window.location.search);
      root.dataset.entryPage =
        params.get('entry_page') || root.dataset.entryPage || 'direct';
      root.dataset.returnUrl =
        params.get('return_url') || root.dataset.returnUrl || '';
      bindPaywallAccordion(root);
      bindCancelSubscriptionTest(root);
      paywallStates.set(root, {
        membership: getCurrentMembership(),
        plans: PLANS,
        loading: false,
      });
      window.addEventListener('pageshow', (event) => {
        const state = paywallStates.get(root);
        if (!event.persisted || !state?.authRedirectPending) return;
        // 登录取消后缓存会保留旧按钮；只清理登录跳转，不干扰付款中的 loading。
        state.authRedirectPending = false;
        root.classList.remove('is-loading');
        renderPaywall(root);
      });
      const visualRoot = getActivePaywallVisualRoot(root);
      visualRoot.querySelectorAll('[data-billing-cycle]').forEach((control) => {
        control.addEventListener('click', () => {
          root.dataset.billingCycle = control.dataset.billingCycle || 'monthly';
          renderPaywall(root);
        });
        control.addEventListener('keydown', (event) => {
          const controls = Array.from(
            visualRoot.querySelectorAll('[data-billing-cycle]'),
          );
          const currentIndex = controls.indexOf(control);
          let nextIndex = currentIndex;
          if (event.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % controls.length;
          } else if (event.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + controls.length) % controls.length;
          } else if (event.key === 'Home') {
            nextIndex = 0;
          } else if (event.key === 'End') {
            nextIndex = controls.length - 1;
          } else {
            return;
          }
          event.preventDefault();
          controls[nextIndex].focus();
          controls[nextIndex].click();
        });
      });
      renderPaywall(root);
      void loadBasicDailyLimit().then(() => renderPaywall(root));
    });
  }

  async function initMembershipPage(root) {
    window.jjbUtils?.restoreMembershipEntryRedirectGroup?.();
    bindComingSoonOverlay(root);
    setPaywallContentBlocked(root, true);
    document.documentElement.classList.add('jjb-membership-experiment-ready');
    const visual = await window.jjbUtils?.resolveMembershipPaywallVisual?.();
    const variant = visual?.variant || 'legacy';
    if (window.jjbUtils?.applyMembershipPaywallVisual) {
      window.jjbUtils.applyMembershipPaywallVisual(root, variant);
    } else {
      root.dataset.membershipVisualVariant = variant;
      root
        .querySelectorAll('[data-membership-visual-variant]')
        .forEach((container) => {
          const active = container.dataset.membershipVisualVariant === variant;
          container.hidden = !active;
          container.inert = !active;
          if (active) container.removeAttribute('aria-hidden');
          else container.setAttribute('aria-hidden', 'true');
        });
    }
    const shouldResolveVisualMembership =
      variant === 'visual' && isCustomerLoggedIn();
    if (!shouldResolveVisualMembership) showMembershipShell(root);
    bindMembershipShell(root);
    const params = new URLSearchParams(window.location.search);
    if (shouldResolveVisualMembership) {
      await refreshMembership(root, params.get('refresh_membership') === '1');
      showMembershipShell(root);
    }
    const initialTab = resolveInitialTab(
      params,
      root.dataset.defaultMembershipTab,
    );
    setMembershipTab(root, initialTab, false);
    if (
      !shouldResolveVisualMembership &&
      params.get('refresh_membership') === '1'
    ) {
      refreshMembership(root, true);
    }
    trackMembershipEntryView(root, initialTab);
  }

  function init() {
    document
      .querySelectorAll('[data-jjb-membership-paywall]')
      .forEach((root) => initMembershipPage(root));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
